import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import {
  FileCheck,
  ClipboardList,
  Home,
  Landmark,
  GraduationCap,
  FileEdit,
  Activity,
  RefreshCw,
  Search,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Calendar,
  User,
  Users,
  ArrowRight,
  X,
  Image as ImageIcon,
  Video as VideoIcon,
  FileText,
  Sparkles,
  Check,
  MessageSquare,
  Building2,
  Newspaper,
  PlusCircle,
  Trash2,
  Edit3,
  Link2,
  ExternalLink,
  AlignLeft,
  Type,
  Tag,
  List,
  Maximize2,
  Briefcase,
  Phone,
  PanelBottom,
  Quote,
  ShieldCheck,
  ChevronRight,
  Info,
  Layers,
  Power,
  CreditCard
} from 'lucide-react';
import './ApprovalDashboard.css';
import '../website-admin/DashboardOverview.css';
import AdmissionsFeeApprovalsTab from '../principal/AdmissionsFeeApprovals';
import AdmissionsExecutiveOverview from '../principal/AdmissionsExecutiveOverview';
import { cleanGoogleMapsEmbedUrl } from '../../utils/mapUtils';

/**
 * Helper to distinguish Testimonial submissions from Website Menu Content submissions
 */
function isTestimonialSubmission(item) {
  if (!item) return false;
  const fid = String(item.field_id || '').toLowerCase();
  const fn = String(item.field_name || '').toLowerCase();
  const st = String(item.section_type || '').toLowerCase();
  const p = String(item.page || '').toLowerCase();
  return (
    fid.startsWith('testimonials.') ||
    fid.includes('testimonial') ||
    st === 'testimonials' ||
    st === 'contact-testimonials' ||
    p === 'testimonials' ||
    fn.includes('testimonial')
  );
}

/**
 * Menu mapping helper to identify menu category, icon, and color badge
 */
function getMenuCategory(page, sectionType, fieldName = '', fieldId = '') {
  const p = (page || '').toLowerCase();
  const s = (sectionType || '').toLowerCase();
  const fn = (fieldName || '').toLowerCase();
  const fid = (fieldId || '').toLowerCase();

  if (p.includes('footer') || s.includes('footer')) {
    return { name: 'Footer', key: 'footer', color: '#475569', bg: '#f8fafc', border: '#cbd5e1', icon: PanelBottom };
  }

  // Smart detect submissions from News Hero even if legacy row recorded page: 'homepage' or section_type: 'hero'
  const isNewsHeroSlide = (s === 'hero' || s === 'news-hero' || s === 'news-updates-hero') && 
                          (fn.includes('news') || (p === 'homepage' && (fid.startsWith('slides.') || fn.includes('slide'))));

  if (p === 'news-updates' || p === 'news-and-updates' || (p === 'news' && s !== 'news') || 
      s.startsWith('news-') || s.startsWith('news_') || s.includes('article') || s.includes('press') || 
      isNewsHeroSlide) {
    return { name: 'News & Updates', key: 'news-updates', color: '#0284c7', bg: '#f0f9ff', border: '#bae6fd', icon: Newspaper };
  }
  if (p === 'aboutus' || p === 'about-us' || s.startsWith('about-us') || s.startsWith('aboutus')) {
    return { name: 'About Us', key: 'about-us', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', icon: Landmark };
  }
  if (p === 'education-program' || p === 'education' || s.startsWith('education') || s.startsWith('edu-') || s.includes('secondary')) {
    return { name: 'Education Program', key: 'education-program', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', icon: GraduationCap };
  }
  if (p === 'admissions' || s.startsWith('admissions') || s.startsWith('adm-')) {
    return { name: 'Admissions', key: 'admissions', color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: FileEdit };
  }
  if (p === 'student-life' || p === 'studentlife' || s.startsWith('student-life') || s.startsWith('studentlife') || s.startsWith('life-') || s.includes('house')) {
    return { name: 'Student Life', key: 'student-life', color: '#db2777', bg: '#fdf2f8', border: '#fbcfe8', icon: Activity };
  }
  if (p === 'facilities' || p === 'facility' || s.startsWith('facilities') || s.startsWith('facility')) {
    return { name: 'Facilities', key: 'facilities', color: '#0284c7', bg: '#f0f9ff', border: '#bae6fd', icon: Building2 };
  }
  if (p === 'parents' || p === 'parent' || s.startsWith('parents') || s.startsWith('parent') || s.includes('palc') || s.includes('handbook')) {
    return { name: 'Parents', key: 'parents', color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe', icon: Users };
  }
  if (p === 'careers' || p === 'career' || s.startsWith('careers') || s.startsWith('career') || s.includes('hiring') || s.includes('position') || s.includes('internship')) {
    return { name: 'Careers', key: 'careers', color: '#4f46e5', bg: '#eef2ff', border: '#c7d2fe', icon: Briefcase };
  }
  if (p === 'contact-us' || p === 'contact' || s.startsWith('contact') || s.includes('faq') || s.includes('partnership') || s.includes('testimonial') || fid.startsWith('testimonials.')) {
    return { name: 'Contact Us', key: 'contact-us', color: '#0d9488', bg: '#f0fdfa', border: '#99f6e4', icon: Phone };
  }
  return { name: 'Homepage', key: 'homepage', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: Home };
}

/**
 * Human-readable section name formatter
 */
function getSectionDisplayName(sectionType, fieldName = '', fieldId = '') {
  const s = (sectionType || '').toLowerCase();
  const fn = (fieldName || '').toLowerCase();
  const fid = (fieldId || '').toLowerCase();

  // Admissions Specific Sections
  if (s === 'admissions-hero' || (s.includes('admissions') && (s.includes('hero') || fn.includes('hero')))) return 'Admissions Hero Section';
  if (s === 'admissions-levels' || (s.includes('admissions') && s.includes('level'))) return 'Admission Levels';
  if (s === 'admissions-requirements' || (s.includes('admissions') && s.includes('requirement'))) return 'Admissions Requirements';
  if (s === 'admissions-process' || (s.includes('admissions') && s.includes('process'))) return 'Admissions Application Process';
  if (s === 'admissions-fees' || s === 'admissions-fees-payment' || (s.includes('admissions') && s.includes('fee'))) return 'Fees & Payment Information';
  if (s === 'admissions-dates' || s === 'admissions-key-dates' || (s.includes('admissions') && (s.includes('date') || s.includes('timeline')))) return 'Admissions Timeline & Key Dates';
  if (s === 'admissions-online-application' || s === 'admissions-online-app' || (s.includes('admissions') && (s.includes('online') || s.includes('call-to-action') || s.includes('cta')))) return 'Online Application Call to Action';
  if (s.includes('admissions') && s.includes('scholarship')) return 'Scholarships & Aid';

  // Student Life Specific Sections
  if (s === 'student-life-hero' || s === 'studentlife-hero' || ((s.includes('student-life') || s.includes('studentlife')) && s.includes('hero'))) return 'Student Life Hero Section';
  if (s === 'student-life-clubs' || s === 'studentlife-clubs' || ((s.includes('student-life') || s.includes('studentlife')) && s.includes('club'))) return 'Clubs & Activities';
  if (s === 'student-life-events' || s === 'studentlife-events' || ((s.includes('student-life') || s.includes('studentlife')) && (s.includes('event') || s.includes('competition')))) return 'Events & Competitions';
  if (s === 'student-life-house-system' || s === 'studentlife-house-system' || ((s.includes('student-life') || s.includes('studentlife')) && (s.includes('house') || s.includes('team')))) return 'House System & Teams';
  if (s === 'student-life-wellbeing' || s === 'studentlife-wellbeing' || ((s.includes('student-life') || s.includes('studentlife')) && (s.includes('wellbeing') || s.includes('sel')))) return 'Wellbeing & Social Emotional Learning';
  if (s === 'student-life-character-shaping' || s === 'studentlife-character-shaping' || ((s.includes('student-life') || s.includes('studentlife')) && (s.includes('character') || s.includes('shaping')))) return 'Character Shaping Program';
  if (s === 'student-life-leaps' || s === 'studentlife-leaps' || ((s.includes('student-life') || s.includes('studentlife')) && s.includes('leap'))) return 'LEAPS Framework';

  if (s === 'facilities-hero' || s === 'facilities-overview-hero' || ((s.includes('facilities') || s.includes('facility')) && s.includes('hero'))) return 'Facilities Hero Section';

  // Parents Specific Sections
  if (s === 'parents-hero' || ((s.includes('parents') || s.includes('parent')) && s.includes('hero'))) return 'Parents Hero Section';
  if (s === 'parents-palc' || s === 'parents-learning-community' || s === 'palc') return 'Learning Community (PaLC)';
  if (s === 'parents-handbook' || s === 'parent-student-handbook' || s === 'handbook') return 'Parent & Student Handbook';
  if (s === 'parents-portal' || s === 'parent-portal-access' || s === 'portal') return 'Parent Portal Access';
  if (s === 'parents-communication' || s === 'communication-channels' || s === 'communication') return 'Communication Channels';

  if (s === 'news-hero' || s === 'news-updates-hero' || ((s.includes('news') || s.includes('updates')) && s.includes('hero'))) return 'News & Updates Hero Section';
  if (s === 'news-articles' || s === 'articles' || s === 'news-blog-articles' || (s.includes('news') && s.includes('article'))) return 'News & Blog Articles';
  if (s === 'news-press' || s === 'press' || s === 'news-press-releases' || (s.includes('news') && s.includes('press'))) return 'Press Releases';

  // Careers Specific Sections
  if (s === 'careers-hero' || ((s.includes('careers') || s.includes('career')) && s.includes('hero'))) return 'Careers Hero Section';
  if (s === 'careers-philosophy' || s === 'hiring-philosophy' || (s.includes('career') && s.includes('philosophy'))) return 'Hiring Philosophy & Culture';
  if (s === 'careers-positions' || s === 'open-positions' || (s.includes('career') && s.includes('position'))) return 'Open Positions & Vacancies';
  if (s === 'careers-opportunities' || s === 'internships-volunteering' || (s.includes('career') && (s.includes('intern') || s.includes('opportunit') || s.includes('volunteer')))) return 'Internships & Volunteering';

  // Contact Us Specific Sections
  if (s === 'contact-hero' || ((s.includes('contact') || s.includes('kontak')) && s.includes('hero'))) return 'Contact Us Hero Section';
  if (s === 'contact-info' || s === 'contact-location' || (s.includes('contact') && (s.includes('info') || s.includes('location')))) return 'Contact Info & Campus Location';
  if (s === 'contact-faq' || s === 'faq' || (s.includes('contact') && s.includes('faq'))) return 'Frequently Asked Questions (FAQ)';
  if (s === 'contact-partnerships' || s === 'partnerships' || (s.includes('contact') && s.includes('partner'))) return 'Partnerships & Affiliations';
  if (s === 'contact-testimonials' || (s.includes('contact') && s.includes('testimoni'))) return 'Testimonials Section';
  if (s === 'contact-countdown' || (s.includes('contact') && (s.includes('countdown') || s.includes('construction')))) return 'Campus Construction & Countdown';
  if (s.includes('hero') || ((s === 'hero' || s === '') && (fn.includes('hero') || fid.startsWith('slides.')))) return 'Hero Section';
  if (s.includes('quick_links') || s.includes('quicklinks')) return 'Quick Links';
  if (s.includes('highlight')) return 'Highlights';
  if (s.includes('news')) return 'News & Updates';
  if (s.includes('countdown')) return 'Countdown';
  if (s.includes('leadership')) return 'School Leadership';
  if (s.includes('principal')) return 'Principal Message';
  if (s.includes('values')) return 'Vision, Mission & Values';
  if (s.includes('philosophy')) return 'School Philosophy';
  if (s.includes('history')) return 'School History';
  if (s.includes('curriculum')) return 'Curriculum Overview';
  if (s.includes('subject')) return 'Subjects Offered';
  if (s === 'education-program-levels-shell' || s === 'education-program-levels' || (s.includes('education') && s.includes('level'))) return 'Academic Pathways';
  if (s.includes('levels') || s.includes('pathway')) return 'Academic Pathways';
  if (s.includes('program')) return 'Program Levels';
  if (s.includes('assessment')) return 'Assessment System';
  if (s.includes('requirement')) return 'Admission Requirements';
  if (s.includes('date') || s.includes('timeline')) return 'Key Dates';
  if (s.includes('fee') || s.includes('payment')) return 'Fees & Payment';
  if (s.includes('process') || s.includes('step')) return 'Admission Process';
  if (s.includes('club')) return 'Clubs & Activities';
  if (s.includes('event')) return 'Events & Competitions';
  if (s.includes('house')) return 'House System';
  if (s.includes('wellbeing')) return 'Wellbeing & SEL';
  if (s.includes('character')) return 'Character Shaping';
  if (s.includes('leap')) return 'LEAPS Framework';
  if (s.includes('facilities-overview') || s.includes('campus-overview')) return 'Campus Facility Overview';
  if (s.includes('facilities-tech') || s.includes('technology')) return 'Technology & Innovation';
  if (s.includes('facilities-safety') || s.includes('safety')) return 'Safety & Security';
  if (s.includes('facilities-gallery') || s.includes('gallery')) return 'Gallery Photographs';
  if (s.includes('facilities-tour') || s.includes('virtual-tour')) return 'Virtual Campus Tour';

  return (sectionType || 'Section')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Operation detector: Add New Item (+), Update (✎), or Delete (✕)
 */
function getOperationInfo(fieldId, oldValue, newValue, fieldName) {
  const fid = (fieldId || '').toLowerCase();
  const fname = (fieldName || '').toLowerCase();

  let parsedNew = newValue;
  if (typeof newValue === 'string' && newValue.trim().startsWith('{')) {
    try {
      parsedNew = JSON.parse(newValue);
    } catch (_) {}
  }

  if (fid.endsWith('.unpublish') || fname.includes('unpublish') || parsedNew?.action === 'unpublish') {
    return {
      type: 'unpublish',
      label: 'Unpublish Item',
      shortLabel: 'UNPUBLISH',
      actionTitle: 'Nonaktifkan (OFF)',
      color: '#dc2626',
      bg: '#fef2f2',
      border: '#fecaca',
      iconText: 'Unpublish (OFF)',
      icon: EyeOff
    };
  }

  if (fid.endsWith('.publish') || fname.startsWith('publish') || parsedNew?.action === 'publish') {
    return {
      type: 'publish',
      label: 'Publish Item',
      shortLabel: 'PUBLISH',
      actionTitle: 'Publikasikan (ON)',
      color: '#16a34a',
      bg: '#f0fdf4',
      border: '#bbf7d0',
      iconText: 'Publish (ON)',
      icon: CheckCircle2
    };
  }

  // 1. Explicit operation indicators
  const isExplicitUpdate = fid.endsWith('.update') || fid.includes('.update') || fid.includes('.edit') || 
                           fname.startsWith('update') || fname.startsWith('ubah') || fname.startsWith('edit');
  const isExplicitDelete = fid.endsWith('.delete') || fid.includes('.delete') || 
                           fname.startsWith('delete') || fname.startsWith('hapus');
  const isExplicitAdd = fid.endsWith('.add') || fid.includes('.add') || 
                        fname.startsWith('add') || fname.startsWith('tambah');

  // If explicitly an update, it is always an UPDATE
  if (isExplicitUpdate) {
    return {
      type: 'update',
      label: 'Update Content',
      shortLabel: 'UPDATE',
      actionTitle: 'Ubah Item / Konten',
      color: '#2563eb',
      bg: '#eff6ff',
      border: '#bfdbfe',
      iconText: '✎ Ubah Item',
      icon: Edit3
    };
  }

  // If explicitly an add
  if (isExplicitAdd) {
    return {
      type: 'add',
      label: 'Add New Item',
      shortLabel: 'ADD',
      actionTitle: 'Tambah Item Baru',
      color: '#16a34a',
      bg: '#f0fdf4',
      border: '#bbf7d0',
      iconText: '+ Tambah Item',
      icon: PlusCircle
    };
  }

  // If explicitly a delete
  if (isExplicitDelete) {
    return {
      type: 'delete',
      label: 'Delete Item',
      shortLabel: 'DELETE',
      actionTitle: 'Hapus Item',
      color: '#dc2626',
      bg: '#fef2f2',
      border: '#fecaca',
      iconText: '✕ Hapus Item',
      icon: Trash2
    };
  }

  // 2. Collection Item Detection (e.g. slides.0, items.1, cards.2)
  const isCollectionItem = /^(slides|items|cards|programs|milestones|faqs|partners|testimonials)\.\d+$/i.test(fieldId || '');

  const isOldValueEmpty = oldValue === null || oldValue === undefined || oldValue === '' || oldValue === '{}' || 
                         oldValue === 'null' || (typeof oldValue === 'object' && Object.keys(oldValue || {}).length === 0);
  const isNewValueEmpty = newValue === null || newValue === undefined || newValue === '' || newValue === '{}' || 
                         newValue === 'null' || (typeof newValue === 'object' && Object.keys(newValue || {}).length === 0);

  // If it is a collection item and the item itself was cleared/deleted -> Delete
  if (isCollectionItem && !isOldValueEmpty && isNewValueEmpty) {
    return {
      type: 'delete',
      label: 'Delete Item',
      shortLabel: 'DELETE',
      actionTitle: 'Hapus Item',
      color: '#dc2626',
      bg: '#fef2f2',
      border: '#fecaca',
      iconText: '✕ Hapus Item',
      icon: Trash2
    };
  }

  // If previous value was empty and new value is provided -> Add
  if (isOldValueEmpty && !isNewValueEmpty) {
    return {
      type: 'add',
      label: 'Add New Item',
      shortLabel: 'ADD',
      actionTitle: 'Tambah Item Baru',
      color: '#16a34a',
      bg: '#f0fdf4',
      border: '#bbf7d0',
      iconText: '+ Tambah Item',
      icon: PlusCircle
    };
  }

  // Default: Any modification or content change is an UPDATE
  return {
    type: 'update',
    label: 'Update Content',
    shortLabel: 'UPDATE',
    actionTitle: 'Ubah Item / Konten',
    color: '#2563eb',
    bg: '#eff6ff',
    border: '#bfdbfe',
    iconText: '✎ Ubah Item',
    icon: Edit3
  };
}

/**
 * Clean field name formatter
 */
function formatFieldDisplayName(fieldName, fieldId) {
  if (fieldId === 'zones.easternCulture' || fieldId === 'zones.easternCulture.update') return 'Eastern Culture Value (Roots)';
  if (fieldId === 'zones.sel' || fieldId === 'zones.sel.update') return 'Social Emotional Learning (Trunk)';
  if (fieldId === 'zones.wellbeing' || fieldId === 'zones.wellbeing.update') return 'Wellbeing (Leaves)';
  if (fieldId === 'programs') return 'LEAPS Dimensions';
  if (fieldName && fieldName.trim() !== '') {
    return fieldName.replace(/^(Add|Delete|Tambah|Hapus)\s+([^:]+):\s*/i, '$2: ');
  }
  if (!fieldId) return 'Main Content';

  return fieldId
    .replace(/\.(add|update|delete)$/, '')
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Safe and resilient image preview component for approval diffs
 */
function SafeImagePreview({
  src,
  rawMediaRef = '',
  alt = 'Preview Photo',
  isBefore = false,
  isImgDifferent = false,
  onOpenLightbox = null,
  containerStyle = {},
  imageStyle = {}
}) {
  const [loadState, setLoadState] = useState('loading'); // 'loading' | 'loaded' | 'error'
  const [activeSrc, setActiveSrc] = useState(src || '');
  const [retryAttempt, setRetryAttempt] = useState(0);

  useEffect(() => {
    setActiveSrc(src || '');
    setLoadState(src ? 'loading' : 'error');
    setRetryAttempt(0);
  }, [src]);

  const handleImageError = () => {
    if (retryAttempt === 0 && activeSrc) {
      setRetryAttempt(1);
      const sep = activeSrc.includes('?') ? '&' : '?';
      setActiveSrc(`${activeSrc}${sep}_t=${Date.now()}`);
      return;
    }
    setLoadState('error');
  };

  const handleManualRetry = (e) => {
    e.stopPropagation();
    setLoadState('loading');
    const sep = (src || '').includes('?') ? '&' : '?';
    setActiveSrc(`${src || activeSrc}${sep}_retry=${Date.now()}`);
  };

  const borderColor = isImgDifferent
    ? (isBefore ? '#ef4444' : '#10b981')
    : '#cbd5e1';

  const bgColor = isImgDifferent
    ? (isBefore ? '#fef2f2' : '#f0fdf4')
    : '#ffffff';

  const headerTitle = isBefore
    ? 'Current Published Photo'
    : (isImgDifferent ? 'Proposed New Photo' : 'Slide Photo');

  const badgeText = isImgDifferent
    ? (isBefore ? 'ORIGINAL' : 'NEW PHOTO')
    : '';

  return (
    <div
      className={`preview-image-container mb-3 ${isImgDifferent ? (isBefore ? 'field-card-diff-before' : 'field-card-diff-after') : ''}`}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: '10px',
        overflow: 'hidden',
        border: `2px solid ${borderColor}`,
        padding: '10px',
        backgroundColor: bgColor,
        transition: 'all 0.2s ease',
        ...containerStyle
      }}
    >
      {/* HEADER BAR */}
      <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 4px 8px' }}>
        <span style={{
          fontSize: '11px',
          fontWeight: '700',
          color: isImgDifferent ? (isBefore ? '#b91c1c' : '#15803d') : '#475569',
          textTransform: 'uppercase',
          letterSpacing: '0.04em'
        }}>
          {headerTitle}
        </span>
        {badgeText && (
          <span style={{
            fontSize: '10px',
            padding: '2px 8px',
            borderRadius: '999px',
            backgroundColor: isBefore ? '#fee2e2' : '#dcfce7',
            color: isBefore ? '#b91c1c' : '#15803d',
            fontWeight: '800',
            letterSpacing: '0.04em'
          }}>
            {badgeText}
          </span>
        )}
      </div>

      {/* IMAGE CONTAINER WITH LOADING & ERROR OVERLAYS */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '190px',
        borderRadius: '6px',
        overflow: 'hidden',
        backgroundColor: '#0f172a08',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* LOADING SPINNER */}
        {loadState === 'loading' && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isBefore ? '#fff5f5' : '#f0fdf4',
            zIndex: 2,
            gap: '8px',
            color: '#64748b',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            <RefreshCw size={22} className="spinning" style={{ color: isImgDifferent ? (isBefore ? '#ef4444' : '#10b981') : '#3b82f6' }} />
            <span>Memuat foto pratinjau...</span>
          </div>
        )}

        {/* ERROR / FALLBACK STATE */}
        {loadState === 'error' && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            textAlign: 'center',
            backgroundColor: '#ffffff',
            zIndex: 3,
            gap: '8px'
          }}>
            <AlertTriangle size={24} style={{ color: '#d97706' }} />
            <div style={{ fontSize: '12.5px', fontWeight: '700', color: '#1e293b' }}>
              Pratinjau foto belum dapat dimuat langsung
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', maxWidth: '320px', wordBreak: 'break-all' }}>
              {rawMediaRef || activeSrc || '(Tidak ada path foto)'}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
              <button
                type="button"
                onClick={handleManualRetry}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '5px 10px',
                  fontSize: '11.5px',
                  fontWeight: '600',
                  color: '#1e293b',
                  background: '#f1f5f9',
                  border: '1px solid #cbd5e1',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                <RefreshCw size={12} />
                <span>Coba Muat Ulang</span>
              </button>
              {activeSrc && (
                <a
                  href={activeSrc}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '5px 10px',
                    fontSize: '11.5px',
                    fontWeight: '600',
                    color: '#2563eb',
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '5px',
                    textDecoration: 'none'
                  }}
                >
                  <ExternalLink size={12} />
                  <span>Buka Tab Baru</span>
                </a>
              )}
            </div>
          </div>
        )}

        {/* ACTUAL IMAGE */}
        {activeSrc && (
          <img
            src={activeSrc}
            alt={alt}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '6px',
              display: loadState === 'error' ? 'none' : 'block',
              opacity: loadState === 'loaded' ? 1 : 0.4,
              transition: 'opacity 0.25s ease',
              ...imageStyle
            }}
            onLoad={() => setLoadState('loaded')}
            onError={handleImageError}
          />
        )}

        {/* FULLSCREEN LIGHTBOX BUTTON ON HOVER */}
        {loadState === 'loaded' && onOpenLightbox && (
          <button
            type="button"
            onClick={() => onOpenLightbox(activeSrc, headerTitle)}
            title="Lihat foto resolusi penuh"
            style={{
              position: 'absolute',
              bottom: '8px',
              right: '8px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              fontSize: '11px',
              fontWeight: '700',
              color: '#ffffff',
              backgroundColor: 'rgba(15, 23, 42, 0.75)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              backdropFilter: 'blur(4px)',
              transition: 'background-color 0.2s ease',
              zIndex: 4
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(15, 23, 42, 0.95)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(15, 23, 42, 0.75)'}
          >
            <Maximize2 size={12} />
            <span>Lihat Penuh</span>
          </button>
        )}
      </div>

      {/* CAPTION BAR */}
      <div className="preview-media-caption" style={{ width: '100%', boxSizing: 'border-box', marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', overflow: 'hidden' }}>
          <ImageIcon size={14} style={{ flexShrink: 0, color: '#64748b' }} />
          <span style={{ fontSize: '11.5px', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Media File: <strong>{String(rawMediaRef || activeSrc)}</strong>
          </span>
        </div>
        {loadState === 'loaded' && activeSrc && (
          <a
            href={activeSrc}
            target="_blank"
            rel="noopener noreferrer"
            title="Buka gambar di tab baru"
            style={{ color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '11px', textDecoration: 'none', flexShrink: 0, marginLeft: '6px' }}
          >
            <ExternalLink size={11} />
            <span>Tab Baru</span>
          </a>
        )}
      </div>
    </div>
  );
}

export default function ApprovalDashboard({ user, onLogout, apiBase, userRole: propUserRole }) {
  const isDirectorUser = propUserRole === 'director' || 
    (Array.isArray(user?.roles) && user.roles.some(r => typeof r === 'string' ? (r.includes('director') || r.includes('school_director')) : (r?.role_code?.includes('director') || r?.role_code?.includes('school_director')))) ||
    (Array.isArray(user?.role_codes) && user.role_codes.some(c => c.includes('director') || c.includes('school_director')));

  const effectiveRole = isDirectorUser ? 'director' : 'approver';
  const [activeTab, setActiveTab] = useState(isDirectorUser ? 'admissions-overview' : 'dashboard');
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view' | 'reject' | 'approve_confirm'
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('pending'); // 'pending' | 'approved' | 'rejected' | 'all'
  const [selectedMenuFilter, setSelectedMenuFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [testimonialStatusFilter, setTestimonialStatusFilter] = useState('pending'); // 'pending' | 'approved' | 'rejected' | 'all'
  const [testimonialActionFilter, setTestimonialActionFilter] = useState('all'); // 'all' | 'publish' | 'unpublish' | 'delete' | 'update'
  const [testimonialSearchQuery, setTestimonialSearchQuery] = useState('');
  const [mediaMap, setMediaMap] = useState({});
  const [testimonialsMap, setTestimonialsMap] = useState({ byId: {}, byAuthor: {} });
  const [toastMessage, setToastMessage] = useState(null);
  const [statusCounts, setStatusCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [lightboxImage, setLightboxImage] = useState(null);
  const [pendingFeeLevels, setPendingFeeLevels] = useState([]);
  const [admissionsSubmenuPending, setAdmissionsSubmenuPending] = useState({
    periods: 0,
    levels: 0,
    test_slots: 0,
    promo_codes: 0,
    total: 0
  });
  const [feeActionLoading, setFeeActionLoading] = useState('');

  const loadPendingFeeLevels = useCallback(async () => {
    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = {};
      if (savedToken) headers['Authorization'] = `Bearer ${savedToken}`;
      const [resLevels, resApprovals] = await Promise.all([
        fetch(`${apiBase}/admin/admissions/levels?_t=${Date.now()}`, { credentials: 'include', headers }),
        fetch(`${apiBase}/admin/admissions/config-approvals?_t=${Date.now()}`, { credentials: 'include', headers })
      ]);

      let pendingLevels = [];
      let pendingConfigs = [];
      if (resLevels.ok) {
        const json = await resLevels.json();
        const raw = json?.data || [];
        const levels = Array.isArray(raw) ? raw : (raw.levels || []);
        pendingLevels = levels.filter(l => l.fee_approval_status === 'pending_director' || l.fee_approval_status === 'pending_principal' || Boolean(l.pending_fee_payload));
      }

      if (resApprovals.ok) {
        const jsonAppr = await resApprovals.json();
        const configs = jsonAppr?.data || [];
        pendingConfigs = configs.filter(c => c.status === 'pending');
      }

      const periodsCount = pendingConfigs.filter(c => c.config_type === 'period').length;
      const slotsCount = pendingConfigs.filter(c => c.config_type === 'test_slot').length;
      const promosCount = pendingConfigs.filter(c => c.config_type === 'promo_code').length;
      const levelsCount = pendingLevels.length + pendingConfigs.filter(c => c.config_type === 'level').length;
      const totalCount = periodsCount + slotsCount + promosCount + levelsCount;

      setAdmissionsSubmenuPending({
        periods: periodsCount,
        levels: levelsCount,
        test_slots: slotsCount,
        promo_codes: promosCount,
        total: totalCount
      });

      setPendingFeeLevels([...pendingLevels, ...pendingConfigs]);
    } catch (err) {
      console.warn('[ApprovalDashboard] Pending fee levels fetch warning:', err);
    }
  }, [apiBase]);

  const handleFeeDecision = useCallback(async (levelCode, decision) => {
    setFeeActionLoading(`${levelCode}_${decision}`);
    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) headers['Authorization'] = `Bearer ${savedToken}`;
      const res = await fetch(`${apiBase}/admin/admissions/levels/${levelCode}/approve-fees`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          decision,
          note: decision === 'approve'
            ? 'Rincian biaya disetujui oleh Kepala Sekolah (Principal).'
            : 'Perubahan rincian biaya dikembalikan oleh Kepala Sekolah untuk ditinjau ulang.'
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        throw new Error(json.error || 'Gagal memproses persetujuan biaya PPDB.');
      }
      setToastMessage({
        text: json.message || (decision === 'approve' ? 'Rincian biaya PPDB berhasil disetujui.' : 'Perubahan rincian biaya PPDB ditolak.'),
        type: 'success'
      });
      setTimeout(() => setToastMessage(null), 4500);
      loadPendingFeeLevels();
    } catch (err) {
      setToastMessage({
        text: err.message || 'Gagal memproses persetujuan biaya PPDB.',
        type: 'error'
      });
      setTimeout(() => setToastMessage(null), 4500);
    } finally {
      setFeeActionLoading('');
    }
  }, [apiBase, loadPendingFeeLevels]);

  const loadTestimonialsMap = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/testimonials?limit=200&_t=${Date.now()}`, {
        credentials: 'include'
      });
      if (!res.ok) return;
      const json = await res.json();
      const list = json?.data?.testimonials || (Array.isArray(json?.data) ? json.data : []);
      const byId = {};
      const byAuthor = {};
      list.forEach(t => {
        if (t.id !== undefined && t.id !== null) {
          byId[String(t.id)] = t;
        }
        if (t.author_name) {
          const norm = String(t.author_name).trim().toLowerCase();
          if (!byAuthor[norm] || t.status === 'approved') {
            byAuthor[norm] = t;
          }
        }
      });
      setTestimonialsMap({ byId, byAuthor });
    } catch (err) {
      console.warn('[ApprovalDashboard] Testimonials lookup warning:', err);
    }
  }, [apiBase]);

  const showToast = useCallback((text, type = 'success') => {
    setToastMessage({ text, type });
    setTimeout(() => {
      setToastMessage(null);
    }, 4500);
  }, []);

  // Fetch all media maps across modules to reliably resolve image IDs
  const loadMediaMap = useCallback(async () => {
    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const fetchHeaders = {};
      if (savedToken) {
        fetchHeaders['Authorization'] = `Bearer ${savedToken}`;
      }

      const endpoints = [
        `${apiBase}/homepage/cms/media?type=image&_t=${Date.now()}`,
        `${apiBase}/homepage/cms/media?_t=${Date.now()}`,
        `${apiBase}/aboutus/cms/media?type=image&_t=${Date.now()}`,
        `${apiBase}/aboutus/cms/media?_t=${Date.now()}`,
        `${apiBase}/education/cms/media?type=image&_t=${Date.now()}`,
        `${apiBase}/admissions/cms/media?type=image&_t=${Date.now()}`,
        `${apiBase}/admissions/cms/media?_t=${Date.now()}`,
        `${apiBase}/studentlife/cms/media?type=image&_t=${Date.now()}`,
      ];

      const responses = await Promise.allSettled(
        endpoints.map(url =>
          fetch(url, { credentials: 'include', headers: fetchHeaders }).then(res => (res.ok ? res.json() : null))
        )
      );

      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      const map = {};
      responses.forEach(result => {
        if (result.status === 'fulfilled' && result.value) {
          const data = result.value.data || result.value;
          const items = Array.isArray(data) ? data : (data.items || []);
          items.forEach(m => {
            const id = m.id || m.media_id;
            const rawUrl = m.url || m.file_path;
            const filename = m.filename || m.file_name;
            if (rawUrl) {
              const fullUrl = rawUrl.startsWith('http') ? rawUrl : (origin ? `${origin}${rawUrl}` : rawUrl);
              if (id) {
                map[String(id)] = fullUrl;
                map[Number(id)] = fullUrl;
              }
              if (filename) {
                map[filename] = fullUrl;
              }
            }
          });
        }
      });

      setMediaMap(prev => ({ ...prev, ...map }));
    } catch (err) {
      console.warn('[ApprovalDashboard] Media map fetch warning:', err);
    }
  }, [apiBase]);

  // Load submissions supporting status filtering with resilient multi-endpoint fallback
  const loadSubmissions = useCallback(async (status = 'all') => {
    setLoading(true);
    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const candidateUrls = [
        `${apiBase}/homepage/cms/field-approvals?status=${status}&limit=200&_t=${Date.now()}`,
        `${apiBase}/submissions/all?status=${status}&limit=200&_t=${Date.now()}`,
        `${apiBase}/submissions/pending?status=${status}&limit=200&_t=${Date.now()}`
      ];

      let loaded = false;
      let lastErr = '';

      for (const candidateUrl of candidateUrls) {
        try {
          const response = await fetch(candidateUrl, {
            method: 'GET',
            credentials: 'include',
            headers,
          });

          if (!response.ok) {
            const errJson = await response.json().catch(() => ({}));
            if (!lastErr) lastErr = errJson.error || `HTTP ${response.status}`;
            continue;
          }

          const result = await response.json().catch(() => null);
          if (!result || !Array.isArray(result.data)) continue;

          const data = result.data.map(it => ({
            ...it,
            id: Number(it.id),
            section_type: it.section_type || it.section_name || 'general',
            page: it.page || it.change_type || 'homepage',
            field_id: it.field_id || it.field_name || 'content',
            field_name: it.field_name || it.field_id || 'Content',
            status: it.status || 'pending'
          }));

          setAllSubmissions(data);

          if (result.status_counts) {
            setStatusCounts(result.status_counts);
          } else {
            const counts = { pending: 0, approved: 0, rejected: 0 };
            data.forEach(item => {
              if (counts[item.status] !== undefined) counts[item.status] += 1;
            });
            setStatusCounts(counts);
          }
          setError('');
          loaded = true;
          break;
        } catch (innerErr) {
          if (!lastErr) lastErr = innerErr.message || 'Network error';
        }
      }

      if (!loaded) {
        setError(lastErr || 'Failed to load submissions');
      }
    } catch (err) {
      setError(err.message || 'Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadSubmissions('all');
    loadMediaMap();
    loadTestimonialsMap();
    loadPendingFeeLevels();
  }, [loadSubmissions, loadMediaMap, loadTestimonialsMap, loadPendingFeeLevels]);

  // Separate Website Menu Content Submissions vs Testimonials Submissions
  const contentSubmissions = useMemo(
    () => allSubmissions.filter(item => !isTestimonialSubmission(item)),
    [allSubmissions]
  );

  const testimonialSubmissions = useMemo(
    () => allSubmissions.filter(item => isTestimonialSubmission(item)),
    [allSubmissions]
  );

  const contentStatusCounts = useMemo(() => {
    const counts = { pending: 0, approved: 0, rejected: 0 };
    contentSubmissions.forEach(item => {
      if (counts[item.status] !== undefined) counts[item.status] += 1;
    });
    return counts;
  }, [contentSubmissions]);

  const testimonialStatusCounts = useMemo(() => {
    const counts = { pending: 0, approved: 0, rejected: 0 };
    testimonialSubmissions.forEach(item => {
      if (counts[item.status] !== undefined) counts[item.status] += 1;
    });
    return counts;
  }, [testimonialSubmissions]);

  // Extract structured Author, Role, and Quote Preview for Testimonial Submissions
  const extractTestimonialInfo = useCallback(
    item => {
      const fid = String(item?.field_id || '');
      const fn = String(item?.field_name || '');
      let parsedNew = item?.new_value;
      let parsedOld = item?.old_value;
      if (typeof parsedNew === 'string') {
        try {
          parsedNew = JSON.parse(parsedNew);
        } catch (_) {}
      }
      if (typeof parsedOld === 'string') {
        try {
          parsedOld = JSON.parse(parsedOld);
        } catch (_) {}
      }

      let tId = null;
      const match = fid.match(/^testimonials\.(\d+)/i);
      if (match) {
        tId = match[1];
      } else if (parsedNew && typeof parsedNew === 'object' && parsedNew.id) {
        tId = String(parsedNew.id);
      } else if (parsedOld && typeof parsedOld === 'object' && parsedOld.id) {
        tId = String(parsedOld.id);
      }

      let authorName =
        (parsedNew && typeof parsedNew === 'object' ? parsedNew.author_name || parsedNew.name || parsedNew.author : null) ||
        (parsedOld && typeof parsedOld === 'object' ? parsedOld.author_name || parsedOld.name || parsedOld.author : null) ||
        null;

      if (!authorName && fn.includes(':')) {
        authorName = fn.split(':').slice(1).join(':').trim();
      }

      const fromId = tId && testimonialsMap?.byId ? testimonialsMap.byId[String(tId)] : null;
      const fromAuthor =
        authorName && testimonialsMap?.byAuthor
          ? testimonialsMap.byAuthor[String(authorName).trim().toLowerCase()]
          : null;
      const matchedRecord = fromId || fromAuthor || null;

      if (!authorName && matchedRecord?.author_name) {
        authorName = matchedRecord.author_name;
      }

      const rawRole =
        (parsedNew && typeof parsedNew === 'object' ? parsedNew.author_type || parsedNew.role : null) ||
        (parsedOld && typeof parsedOld === 'object' ? parsedOld.author_type || parsedOld.role : null) ||
        matchedRecord?.author_type ||
        (fid.startsWith('testimonials.') ? 'Community Member' : 'Section Setting');

      let quoteText =
        (parsedNew && typeof parsedNew === 'object' ? parsedNew.content || parsedNew.quote || parsedNew.text : null) ||
        (parsedOld && typeof parsedOld === 'object' ? parsedOld.content || parsedOld.quote || parsedOld.text : null) ||
        matchedRecord?.content ||
        '';

      if (typeof quoteText === 'string' && quoteText.includes('[IMAGE:')) {
        quoteText = quoteText.replace(/\[IMAGE:https?:\/\/[^\]]+\]\s*/g, '').trim();
      }

      if (
        !quoteText &&
        typeof parsedNew === 'string' &&
        !['approved', 'pending', 'rejected', 'publish', 'unpublish'].includes(parsedNew.toLowerCase())
      ) {
        quoteText = parsedNew;
      }

      const isSectionField = !fid.startsWith('testimonials.') && !tId && !authorName;

      return {
        testimonialId: tId,
        authorName:
          authorName ||
          (isSectionField ? 'Testimonials Section Header' : formatFieldDisplayName(item.field_name, item.field_id)),
        authorRole: isSectionField
          ? 'Section Header / Copy'
          : String(rawRole || 'Parent / Student').replace(/\b\w/g, c => c.toUpperCase()),
        quoteText:
          quoteText ||
          (isSectionField
            ? `Perubahan teks/konfigurasi pada field: ${formatFieldDisplayName(item.field_name, item.field_id)}`
            : 'Klik Review untuk memeriksa perbandingan kutipan testimoni secara lengkap.'),
        isSectionField
      };
    },
    [testimonialsMap]
  );

  // On-demand media lookup if selected item contains an unmapped numeric media ID
  useEffect(() => {
    if (!selectedItem) return;
    const extractNumericId = val => {
      const parsed = parseJsonValue(val);
      if (!parsed) return null;
      if (typeof parsed === 'number' || /^\d+$/.test(String(parsed).trim())) {
        return Number(parsed);
      }
      if (typeof parsed === 'object') {
        const img = parsed.image || parsed.photo || parsed.thumbnail || parsed.media_id || parsed.mediaId;
        if (img && /^\d+$/.test(String(img).trim())) {
          return Number(img);
        }
      }
      return null;
    };

    const newId = extractNumericId(selectedItem.new_value);
    const oldId = extractNumericId(selectedItem.old_value);
    const missing = [newId, oldId].filter(id => id && !mediaMap[id] && !mediaMap[String(id)]);
    if (missing.length > 0) {
      loadMediaMap();
    }
  }, [selectedItem, mediaMap, loadMediaMap]);

  // Statistics calculation for Menu counters (Pending & Approved per module, excluding testimonials)
  const menuStats = useMemo(() => {
    const counts = {
      all: 0,
      'homepage': 0,
      'about-us': 0,
      'education-program': 0,
      'admissions': 0,
      'student-life': 0,
      'facilities': 0,
      'news-updates': 0,
      'parents': 0,
      'careers': 0,
      'contact-us': 0,
      'footer': 0,
      approvedByMenu: {}
    };

    contentSubmissions.forEach(item => {
      const menu = getMenuCategory(item.page, item.section_type, item.field_name, item.field_id);
      if (item.status === 'pending') {
        counts.all += 1;
        if (counts[menu.key] !== undefined) {
          counts[menu.key] += 1;
        }
      } else if (item.status === 'approved') {
        counts.approvedByMenu[menu.key] = (counts.approvedByMenu[menu.key] || 0) + 1;
      }
    });

    return counts;
  }, [contentSubmissions]);

  // Filtered Website Menu Submissions (excludes Testimonials)
  const filteredSubmissions = useMemo(() => {
    return contentSubmissions.filter(item => {
      // 1. Status Filter
      if (selectedStatusFilter !== 'all' && item.status !== selectedStatusFilter) {
        return false;
      }

      // 2. Menu Filter
      const menu = getMenuCategory(item.page, item.section_type, item.field_name, item.field_id);
      if (selectedMenuFilter !== 'all' && menu.key !== selectedMenuFilter) {
        return false;
      }

      // 3. Search Query
      if (!searchQuery.trim()) return true;

      const q = searchQuery.toLowerCase();
      const sectionName = getSectionDisplayName(item.section_type, item.field_name, item.field_id).toLowerCase();
      const fieldName = (item.field_name || item.field_id || '').toLowerCase();
      const submitter = (item.submitted_by_name || item.submitted_by_email || '').toLowerCase();
      const idMatch = String(item.id).includes(q);

      return sectionName.includes(q) || fieldName.includes(q) || submitter.includes(q) || idMatch;
    });
  }, [contentSubmissions, selectedStatusFilter, selectedMenuFilter, searchQuery]);

  // Filtered Testimonials Submissions (dedicated Testimonials queue)
  const filteredTestimonialSubmissions = useMemo(() => {
    return testimonialSubmissions.filter(item => {
      if (testimonialStatusFilter !== 'all' && item.status !== testimonialStatusFilter) {
        return false;
      }

      const op = getOperationInfo(item.field_id, item.old_value, item.new_value, item.field_name);
      if (testimonialActionFilter !== 'all' && op.type !== testimonialActionFilter) {
        return false;
      }

      if (!testimonialSearchQuery.trim()) return true;

      const q = testimonialSearchQuery.toLowerCase();
      const tInfo = extractTestimonialInfo(item);
      const idMatch = String(item.id).includes(q);
      const authorMatch = (tInfo.authorName || '').toLowerCase().includes(q);
      const roleMatch = (tInfo.authorRole || '').toLowerCase().includes(q);
      const quoteMatch = (tInfo.quoteText || '').toLowerCase().includes(q);
      const submitterMatch = (item.submitted_by_name || '').toLowerCase().includes(q);
      const fieldMatch = (item.field_name || item.field_id || '').toLowerCase().includes(q);

      return idMatch || authorMatch || roleMatch || quoteMatch || submitterMatch || fieldMatch;
    });
  }, [testimonialSubmissions, testimonialStatusFilter, testimonialActionFilter, testimonialSearchQuery, extractTestimonialInfo]);

  // Approve action
  const handleApprove = async () => {
    if (!selectedItem) return;

    try {
      setActionLoading(true);
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(`${apiBase}/homepage/cms/field-approvals/${selectedItem.id}/approve`, {
        method: 'POST',
        credentials: 'include',
        headers,
      });

      const resData = await response.json().catch(() => ({}));

      if (response.ok && (resData.ok || resData.success)) {
        showToast(`Submission #${selectedItem.id} approved and published live!`, 'success');

        window.dispatchEvent(
          new CustomEvent('approvalCompleted', {
            detail: { itemId: selectedItem.id, type: 'approval', timestamp: Date.now() },
          })
        );
        sessionStorage.setItem('needsCmsRefresh', 'true');
        sessionStorage.setItem('lastApprovalTime', Date.now().toString());

        closeModal();
        loadSubmissions('all');
      } else {
        showToast(resData.error || resData.message || 'Failed to approve changes', 'error');
      }
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Reject action
  const handleReject = async () => {
    if (!selectedItem) return;

    if (!rejectReason.trim() || rejectReason.trim().length < 5) {
      alert('Please specify a rejection reason (minimum 5 characters).');
      return;
    }

    try {
      setActionLoading(true);
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(`${apiBase}/homepage/cms/field-approvals/${selectedItem.id}/reject`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ reason: rejectReason.trim() }),
      });

      const resData = await response.json().catch(() => ({}));

      if (response.ok && (resData.ok || resData.success)) {
        showToast(`Submission #${selectedItem.id} rejected with feedback notes.`, 'success');

        window.dispatchEvent(
          new CustomEvent('approvalCompleted', {
            detail: { itemId: selectedItem.id, type: 'rejection', timestamp: Date.now() },
          })
        );

        closeModal();
        loadSubmissions('all');
      } else {
        showToast(resData.error || resData.message || 'Failed to reject changes', 'error');
      }
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const openReviewModal = (item, mode = 'view') => {
    setSelectedItem(item);
    setModalMode(mode);
    setRejectReason(item.rejected_reason || '');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setModalMode('view');
    setRejectReason('');
    setLightboxImage(null);
  };

  /**
   * Resolve an image ID or file path into a full URL
   */
  const resolveImage = useCallback(
    val => {
      if (!val) return null;
      const origin = (apiBase || '').replace(/\/api\/?$/, '');

      // Helper to prefix origin if relative path
      const withOrigin = path => {
        if (!path) return null;
        if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:image/')) {
          return path;
        }
        if (path.startsWith('/')) {
          return origin ? `${origin}${path}` : path;
        }
        return origin ? `${origin}/${path}` : `/${path}`;
      };

      if (typeof val === 'object') {
        const candidate = val.image || val.photo || val.thumbnail || val.logo || val.url || val.file_path || val.filename || val.file_name;
        if (candidate && typeof candidate === 'string') {
          return resolveImage(candidate);
        }
        return null;
      }

      if (typeof val === 'string') {
        const trimmed = val.trim();
        // If it contains newlines or is longer than 500 chars, it's not a path
        if (trimmed.length > 500 || trimmed.includes('\n')) {
          return null;
        }
        // If it contains spaces and doesn't match a filename with image extension, it's text
        if (trimmed.includes(' ') && !trimmed.match(/^[a-zA-Z0-9_\-\s/.:]+\.(jpg|jpeg|png|webp|svg|gif|avif)$/i)) {
          return null;
        }
        if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:image/')) {
          return trimmed;
        }
        if (mediaMap[trimmed]) {
          return withOrigin(mediaMap[trimmed]);
        }
        if (trimmed.startsWith('/api/uploads/')) {
          return withOrigin(trimmed);
        }
        if (trimmed.startsWith('/uploads/')) {
          return withOrigin(`/api${trimmed}`);
        }
        if (trimmed.startsWith('uploads/')) {
          return withOrigin(`/api/${trimmed}`);
        }
        if (trimmed.startsWith('admissions_')) {
          return withOrigin(`/api/uploads/admissions/${trimmed}`);
        }
        if (trimmed.startsWith('studentlife_') || trimmed.startsWith('student_life_') || trimmed.startsWith('life_')) {
          return withOrigin(`/api/uploads/studentlife/${trimmed}`);
        }
        if (trimmed.startsWith('education_')) {
          return withOrigin(`/api/uploads/homepage/education/${trimmed}`);
        }
        if (trimmed.startsWith('aboutus_')) {
          return withOrigin(`/api/uploads/homepage/about-us/${trimmed}`);
        }
        if (trimmed.startsWith('media_')) {
          return withOrigin(`/api/uploads/homepage/highlights/${trimmed}`);
        }
        if (trimmed.startsWith('cms_media_')) {
          return withOrigin(`/uploads/cms/${trimmed}`);
        }
        if (trimmed.match(/\.(jpg|jpeg|png|webp|svg|gif|avif)$/i)) {
          const sType = (selectedItem?.section_type || '').toLowerCase();
          const pType = (selectedItem?.page || '').toLowerCase();
          if (sType.includes('admissions') || pType.includes('admissions') || trimmed.includes('admission')) {
            return withOrigin(`/api/uploads/admissions/${trimmed}`);
          }
          if (sType.includes('studentlife') || pType.includes('studentlife') || trimmed.includes('studentlife')) {
            return withOrigin(`/api/uploads/studentlife/${trimmed}`);
          }
          if (sType.includes('education') || pType.includes('education') || trimmed.includes('education')) {
            return withOrigin(`/api/uploads/homepage/education/${trimmed}`);
          }
          if (sType.includes('aboutus') || pType.includes('aboutus') || trimmed.includes('aboutus')) {
            return withOrigin(`/api/uploads/homepage/about-us/${trimmed}`);
          }
          return withOrigin(`/api/uploads/images/${trimmed}`);
        }
      }
      if (typeof val === 'number' || /^\d+$/.test(String(val).trim())) {
        const idKey = String(val).trim();
        if (mediaMap[idKey]) return withOrigin(mediaMap[idKey]);
        if (mediaMap[Number(idKey)]) return withOrigin(mediaMap[Number(idKey)]);
      }
      return null;
    },
    [mediaMap, apiBase, selectedItem]
  );

  /**
   * Helper to parse JSON values safely
   */
  const parseJsonValue = val => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'object') return val;
    try {
      return JSON.parse(val);
    } catch {
      return val;
    }
  };

  // Preset Link Destinations for human-friendly preview
  const PRESET_LINK_DESTINATIONS = {
    'register': {
      title: 'New Student Registration Form',
      route: '/register',
      category: 'Registration & Admissions',
      badgeColor: '#2563eb',
      badgeBg: '#eff6ff',
      isExternal: false
    },
    'contact-us#contact-form': {
      title: 'Contact Us - Inquiry Form',
      route: '/contact-us#contact-form',
      category: 'Inquiry & Support',
      badgeColor: '#059669',
      badgeBg: '#ecfdf5',
      isExternal: false
    },
    'contact-us': {
      title: 'Contact Us Main Page',
      route: '/contact-us',
      category: 'Contact Page',
      badgeColor: '#059669',
      badgeBg: '#ecfdf5',
      isExternal: false
    },
    'admissions': {
      title: 'Admissions & Enrollment Information',
      route: '/admissions',
      category: 'Admissions Pathway',
      badgeColor: '#7c3aed',
      badgeBg: '#f5f3ff',
      isExternal: false
    },
    'education-program': {
      title: 'Education Program & Academic Pathways',
      route: '/education-program',
      category: 'Academic Curriculum',
      badgeColor: '#0284c7',
      badgeBg: '#f0f9ff',
      isExternal: false
    },
    'student-life': {
      title: 'Student Life, Clubs & ECA',
      route: '/student-life',
      category: 'Student Activities',
      badgeColor: '#db2777',
      badgeBg: '#fdf2f8',
      isExternal: false
    },
    'facilities': {
      title: 'Campus Facilities Overview',
      route: '/facilities',
      category: 'Campus Facilities',
      badgeColor: '#0d9488',
      badgeBg: '#f0fdfa',
      isExternal: false
    },
    'news-and-updates': {
      title: 'News & Announcements',
      route: '/news-and-updates',
      category: 'News & Events',
      badgeColor: '#ea580c',
      badgeBg: '#fff7ed',
      isExternal: false
    },
    'parents': {
      title: 'Parents Portal & Handbook',
      route: '/parents',
      category: 'Parent Community',
      badgeColor: '#b45309',
      badgeBg: '#fffbeb',
      isExternal: false
    },
    'careers': {
      title: 'Careers & Job Openings',
      route: '/careers',
      category: 'Career Opportunities',
      badgeColor: '#4f46e5',
      badgeBg: '#eef2ff',
      isExternal: false
    },
    'about-us': {
      title: 'About Us & School Profile',
      route: '/about-us',
      category: 'School Profile',
      badgeColor: '#475569',
      badgeBg: '#f8fafc',
      isExternal: false
    },
    'login': {
      title: 'PHS Portal Login Gateway',
      route: '/login',
      category: 'Portal Authentication',
      badgeColor: '#1e293b',
      badgeBg: '#f1f5f9',
      isExternal: false
    },
    '#school-calendar': {
      title: 'School Academic Calendar Section',
      route: '#school-calendar',
      category: 'In-Page Jump Section',
      badgeColor: '#d97706',
      badgeBg: '#fffbeb',
      isExternal: false
    }
  };

  const resolveLinkDestination = rawLink => {
    if (!rawLink || typeof rawLink !== 'string') {
      return {
        title: '(No Destination Configured)',
        route: '',
        category: 'Unset',
        badgeColor: '#94a3b8',
        badgeBg: '#f1f5f9',
        isExternal: false
      };
    }

    const trimmed = rawLink.trim();
    const normalized = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;

    if (PRESET_LINK_DESTINATIONS[normalized]) {
      return PRESET_LINK_DESTINATIONS[normalized];
    }

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      let hostname = trimmed;
      try {
        hostname = new URL(trimmed).hostname;
      } catch (_) {}
      return {
        title: `External Website (${hostname})`,
        route: trimmed,
        category: 'External Link',
        badgeColor: '#2563eb',
        badgeBg: '#eff6ff',
        isExternal: true
      };
    }

    if (trimmed.startsWith('#')) {
      return {
        title: `In-Page Anchor: ${trimmed}`,
        route: trimmed,
        category: 'In-Page Section',
        badgeColor: '#d97706',
        badgeBg: '#fffbeb',
        isExternal: false
      };
    }

    return {
      title: `Internal Route: /${normalized}`,
      route: `/${normalized}`,
      category: 'Internal Page',
      badgeColor: '#475569',
      badgeBg: '#f8fafc',
      isExternal: false
    };
  };

  const getFieldMeta = key => {
    const kLower = (key || '').toLowerCase();
    if (kLower === 'tagline_prefix') {
      return { label: 'Tagline Prefix', icon: Type, isLink: false };
    }
    if (kLower === 'tagline_accent') {
      return { label: 'Tagline Accent', icon: Sparkles, isLink: false };
    }
    if (kLower === 'philosophy') {
      return { label: 'Philosophy Statement', icon: AlignLeft, isLink: false };
    }
    if (kLower === 'video_file' || kLower === 'video_url') {
      return { label: 'Hero Video', icon: VideoIcon, isLink: false };
    }
    if (kLower === 'principal_photo_id' || kLower === 'principal_photo') {
      return { label: 'Principal Portrait Photo', icon: ImageIcon, isLink: false };
    }
    if (kLower === 'title') {
      return { label: 'Title / Headline', icon: Type, isLink: false };
    }
    if (kLower === 'text' || kLower === 'description' || kLower === 'desc') {
      return { label: 'Description', icon: AlignLeft, isLink: false };
    }
    if (kLower === 'link' || kLower === 'url' || kLower === 'href' || kLower === 'button_link' || kLower === 'buttonlink' || kLower === 'path') {
      return { label: 'Link Destination', icon: Link2, isLink: true };
    }
    if (kLower === 'button_text' || kLower === 'buttontext' || kLower === 'btn_text') {
      return { label: 'Button Label', icon: Tag, isLink: false };
    }
    if (kLower === 'subtitle' || kLower === 'sub_title') {
      return { label: 'Subtitle', icon: Type, isLink: false };
    }
    if (kLower === 'countdown_date' || kLower === 'target_date') {
      return { label: 'Target Opening Date', icon: Calendar, isLink: false };
    }
    if (kLower === 'countdown_label') {
      return { label: 'Pill Badge Text', icon: Tag, isLink: false };
    }
    if (kLower === 'countdown_title') {
      return { label: 'Countdown Title', icon: Type, isLink: false };
    }
    if (kLower === 'countdown_description') {
      return { label: 'Countdown Description', icon: AlignLeft, isLink: false };
    }
    if (kLower === 'kicker') {
      return { label: 'Kicker / Eyebrow', icon: Tag, isLink: false };
    }
    if (kLower === 'latest_news_label' || kLower === 'latestnewslabel') {
      return { label: 'Card Header Label', icon: Tag, isLink: false };
    }
    if (kLower === 'intro') {
      return { label: 'Introduction Summary', icon: AlignLeft, isLink: false };
    }
    if (kLower === 'badge' || kLower === 'tag') {
      return { label: 'Category Tag / Kicker', icon: Tag, isLink: false };
    }
    if (kLower === 'icon') {
      return { label: 'Icon Name', icon: Sparkles, isLink: false };
    }
    if (kLower === 'name' || kLower === 'author') {
      return { label: 'Name / Person', icon: User, isLink: false };
    }
    if (kLower === 'role') {
      return { label: 'Designation / Role', icon: User, isLink: false };
    }
    if (kLower === 'date') {
      return { label: 'Date', icon: Calendar, isLink: false };
    }
    if (kLower === 'points' || kLower === 'point') {
      return { label: 'Points / Symbolic Elements', icon: List, isLink: false };
    }
    if (kLower === 'facts' || kLower === 'fact' || kLower === 'statistics' || kLower === 'stats') {
      return { label: 'Institutional Facts & Statistics', icon: List, isLink: false };
    }
    if (kLower === 'milestones' || kLower === 'milestone') {
      return { label: 'Timeline Milestones', icon: Calendar, isLink: false };
    }
    if (kLower === 'tags') {
      return { label: 'Tags / Categories', icon: Tag, isLink: false };
    }
    if (kLower === 'cta') {
      return { label: 'Call to Action / Footer Note', icon: AlignLeft, isLink: false };
    }
    if (kLower === 'headline') {
      return { label: 'Headline', icon: Type, isLink: false };
    }
    if (kLower === 'paragraph') {
      return { label: 'Main Paragraph / Story', icon: AlignLeft, isLink: false };
    }
    if (kLower === 'bio' || kLower === 'biography') {
      return { label: 'Professional Biography', icon: AlignLeft, isLink: false };
    }
    if (kLower === 'note') {
      return { label: 'Special Note / Footnote', icon: AlignLeft, isLink: false };
    }
    if (kLower === 'quote') {
      return { label: 'Quote Statement', icon: AlignLeft, isLink: false };
    }
    if (kLower === 'shorttitle' || kLower === 'short_title') {
      return { label: 'Pill Tab Label', icon: Tag, isLink: false };
    }
    return {
      label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      icon: FileText,
      isLink: kLower.includes('link') || kLower.includes('url') || kLower.includes('href')
    };
  };

  const renderLinkDestinationPreview = (rawLink, isDifferent, isBefore) => {
    const info = resolveLinkDestination(rawLink);

    return (
      <div 
        className="preview-link-card"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          padding: '10px 14px',
          borderRadius: '8px',
          background: isDifferent ? (isBefore ? '#fff5f5' : '#f0fdf4') : '#f8fafc',
          border: `1.5px solid ${isDifferent ? (isBefore ? '#fca5a5' : '#86efac') : '#e2e8f0'}`,
          borderLeft: `4px solid ${isDifferent ? (isBefore ? '#ef4444' : '#10b981') : info.badgeColor}`,
          marginTop: '4px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Link2 size={14} style={{ color: info.badgeColor, flexShrink: 0 }} />
            <strong style={{ fontSize: '13px', color: isDifferent ? (isBefore ? '#991b1b' : '#166534') : '#0f172a' }}>
              {info.title}
            </strong>
          </div>

          <span style={{
            fontSize: '10.5px',
            fontWeight: '700',
            padding: '2px 8px',
            borderRadius: '999px',
            background: info.badgeBg,
            color: info.badgeColor,
            border: `1px solid ${info.badgeColor}33`,
            textTransform: 'uppercase',
            letterSpacing: '0.02em'
          }}>
            {info.category}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#64748b', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: '600' }}>Destination:</span>
          <code style={{
            background: '#ffffff',
            padding: '2px 8px',
            borderRadius: '4px',
            border: '1px solid #cbd5e1',
            color: '#1e293b',
            fontSize: '11.5px',
            fontFamily: 'monospace'
          }}>
            {info.route || '(empty)'}
          </code>
          {info.isExternal && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: '#2563eb', fontSize: '11px', fontWeight: '600' }}>
              <ExternalLink size={11} />
              <span>Opens in new tab</span>
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderTestimonialTogglePreview = (rawValue, isBefore, itemContext) => {
    const parsedVal = parseJsonValue(itemContext?.new_value);
    const parsedOld = parseJsonValue(itemContext?.old_value);
    const currentParsed = parseJsonValue(rawValue);

    const extractedId =
      parsedVal?.id ||
      parsedOld?.id ||
      itemContext?.field_id?.match(/testimonials\.(\d+)/i)?.[1] ||
      null;

    const extractedAuthorRaw =
      parsedVal?.author_name ||
      parsedOld?.author_name ||
      itemContext?.field_name?.match(/:\s*(.+)$/)?.[1] ||
      '';

    const matchedRecord =
      (extractedId && testimonialsMap.byId[String(extractedId)]) ||
      (extractedAuthorRaw && testimonialsMap.byAuthor[String(extractedAuthorRaw).trim().toLowerCase()]) ||
      null;

    const authorName =
      currentParsed?.author_name ||
      parsedVal?.author_name ||
      matchedRecord?.author_name ||
      extractedAuthorRaw ||
      'Testimonial Author';

    const authorType =
      currentParsed?.author_type ||
      parsedVal?.author_type ||
      matchedRecord?.author_type ||
      'Parent / Student';

    const quoteText =
      currentParsed?.content ||
      currentParsed?.quote ||
      parsedVal?.content ||
      parsedVal?.quote ||
      parsedOld?.content ||
      matchedRecord?.content ||
      '';

    const rawImg =
      currentParsed?.image_url ||
      parsedVal?.image_url ||
      matchedRecord?.image_url ||
      null;
    const resolvedImg = rawImg ? resolveImage(rawImg) : null;

    const actionType = (
      parsedVal?.action ||
      (itemContext?.field_id?.toLowerCase().includes('unpublish') || itemContext?.field_name?.toLowerCase().includes('unpublish')
        ? 'unpublish'
        : itemContext?.field_id?.toLowerCase().includes('delete') || itemContext?.field_name?.toLowerCase().includes('delete')
          ? 'delete'
          : 'publish')
    ).toLowerCase();

    const isDeactivating = actionType === 'unpublish' || actionType === 'delete';

    if (isBefore) {
      const wasApproved =
        String(rawValue || '').toLowerCase() === 'approved' ||
        (rawValue === null || rawValue === undefined ? isDeactivating : false);

      return (
        <div className="preview-object-container">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              padding: '16px',
              borderRadius: '10px',
              backgroundColor: '#ffffff',
              border: '1.5px solid #cbd5e1',
              borderLeft: `4px solid ${wasApproved ? '#059669' : '#d97706'}`
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Current Public Website Status
              </span>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: '800',
                  padding: '3px 10px',
                  borderRadius: '999px',
                  backgroundColor: wasApproved ? '#dcfce7' : '#fef3c7',
                  color: wasApproved ? '#166534' : '#92400e',
                  border: `1px solid ${wasApproved ? '#86efac' : '#fde68a'}`
                }}
              >
                {wasApproved ? 'PUBLISHED (ON) - LIVE ON WEBSITE' : 'UNPUBLISHED (OFF) - HIDDEN'}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px', color: '#0f172a' }}>
              <User size={14} style={{ color: '#64748b', flexShrink: 0 }} />
              <span>
                Author: <strong>{authorName}</strong>{' '}
                <span style={{ color: '#64748b', textTransform: 'capitalize' }}>({authorType})</span>
              </span>
            </div>

            <div style={{ padding: '14px 16px', borderRadius: '8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <Quote size={14} style={{ color: '#2563eb', flexShrink: 0 }} />
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {wasApproved ? 'Active Testimonial Text on Public Website:' : 'Current Testimonial Review Text:'}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '14.5px', lineHeight: '1.65', color: '#0f172a', fontStyle: 'italic', fontWeight: '500', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {quoteText ? `"${quoteText}"` : '(Testimonial text is empty)'}
              </p>
            </div>

            {resolvedImg && (
              <SafeImagePreview
                src={resolvedImg}
                rawMediaRef={String(rawImg)}
                alt={authorName}
                isBefore={true}
                isImgDifferent={false}
                onOpenLightbox={(src, title) => setLightboxImage({ src, title })}
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="preview-object-container">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: '16px',
            borderRadius: '10px',
            backgroundColor: isDeactivating ? '#fff5f5' : '#f0fdf4',
            border: `1.5px solid ${isDeactivating ? '#fca5a5' : '#86efac'}`,
            borderLeft: `4px solid ${isDeactivating ? '#ef4444' : '#10b981'}`
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', color: isDeactivating ? '#991b1b' : '#166534', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Proposed Status Change
            </span>
            <span
              style={{
                fontSize: '11px',
                fontWeight: '800',
                padding: '3px 10px',
                borderRadius: '999px',
                backgroundColor: isDeactivating ? '#fee2e2' : '#dcfce7',
                color: isDeactivating ? '#991b1b' : '#166534',
                border: `1px solid ${isDeactivating ? '#fca5a5' : '#86efac'}`
              }}
            >
              {actionType === 'delete'
                ? 'DELETE TESTIMONIAL'
                : isDeactivating
                  ? 'UNPUBLISH (OFF) - DEACTIVATE'
                  : 'PUBLISH (ON) - ACTIVATE'}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px', color: '#0f172a' }}>
            <User size={14} style={{ color: isDeactivating ? '#b91c1c' : '#15803d', flexShrink: 0 }} />
            <span>
              Author: <strong>{authorName}</strong>{' '}
              <span style={{ color: '#64748b', textTransform: 'capitalize' }}>({authorType})</span>
            </span>
          </div>

          <div style={{ fontSize: '12.5px', color: isDeactivating ? '#991b1b' : '#166534', lineHeight: '1.5' }}>
            {actionType === 'delete'
              ? 'This testimonial review text will be permanently deleted upon approval:'
              : isDeactivating
                ? 'This testimonial review text will be deactivated (OFF) and hidden from the public website upon approval:'
                : 'This testimonial review text will be activated (ON) and published on the public website upon approval:'}
          </div>

          <div style={{ padding: '14px 16px', borderRadius: '8px', backgroundColor: '#ffffff', border: `1.5px solid ${isDeactivating ? '#fca5a5' : '#86efac'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <Quote size={14} style={{ color: isDeactivating ? '#dc2626' : '#059669', flexShrink: 0 }} />
              <span style={{ fontSize: '11px', fontWeight: '700', color: isDeactivating ? '#991b1b' : '#166534', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {actionType === 'delete'
                  ? 'Testimonial Text to Be Deleted:'
                  : isDeactivating
                    ? 'Testimonial Text Being Deactivated (OFF):'
                    : 'Testimonial Text Being Published (ON):'}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: '14.5px', lineHeight: '1.65', color: '#0f172a', fontStyle: 'italic', fontWeight: '600', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {quoteText ? `"${quoteText}"` : '(Testimonial text is empty)'}
            </p>
          </div>

          {resolvedImg && (
            <SafeImagePreview
              src={resolvedImg}
              rawMediaRef={String(rawImg)}
              alt={authorName}
              isBefore={false}
              isImgDifferent={false}
              onOpenLightbox={(src, title) => setLightboxImage({ src, title })}
            />
          )}
        </div>

        {typeof currentParsed === 'object' && currentParsed !== null && (
          <details className="raw-json-details">
            <summary>View Raw Data</summary>
            <pre className="raw-json-code">{JSON.stringify(currentParsed, null, 2)}</pre>
          </details>
        )}
      </div>
    );
  };

  /**
   * Comprehensive content value renderer for Before & After with visual diff highlighting
   */
  const renderValuePreview = (rawValue, fieldId = '', isBefore = false, otherValue = null) => {
    const parsed = parseJsonValue(rawValue);
    const otherParsed = parseJsonValue(otherValue);

    const fidLower = (fieldId || '').toLowerCase();
    const fnameLower = (selectedItem?.field_name || '').toLowerCase();
    const isTestimonialAction =
      fidLower.startsWith('testimonials.') ||
      (parsed && typeof parsed === 'object' && (parsed.action === 'unpublish' || parsed.action === 'publish')) ||
      (otherParsed && typeof otherParsed === 'object' && (otherParsed.action === 'unpublish' || otherParsed.action === 'publish')) ||
      fnameLower.includes('unpublish (off)') ||
      fnameLower.includes('publish (on)');

    if (isTestimonialAction) {
      return renderTestimonialTogglePreview(rawValue, isBefore, selectedItem);
    }
    const isOtherEmpty = otherParsed === null || otherParsed === undefined || otherParsed === '' || (typeof otherParsed === 'object' && Object.keys(otherParsed || {}).length === 0);
    const op = getOperationInfo(fieldId, isBefore ? rawValue : otherValue, isBefore ? otherValue : rawValue);

    if (parsed === null || parsed === undefined || parsed === '' || (typeof parsed === 'object' && Object.keys(parsed || {}).length === 0)) {
      if (isBefore) {
        return (
          <div className="empty-value-box" style={{ background: '#f8fafc', border: '1.5px dashed #cbd5e1', padding: '24px 16px', borderRadius: '10px' }}>
            <PlusCircle size={22} style={{ color: '#16a34a', marginBottom: '6px' }} />
            <strong style={{ fontSize: '13px', color: '#166534' }}>Penambahan Item Baru</strong>
            <span style={{ fontSize: '12px', color: '#64748b' }}>Belum ada data sebelumnya di website publik (Item Baru).</span>
          </div>
        );
      }
      if (op.type === 'delete') {
        return (
          <div className="empty-value-box" style={{ background: '#fef2f2', border: '1.5px dashed #fca5a5', padding: '24px 16px', borderRadius: '10px' }}>
            <Trash2 size={22} style={{ color: '#dc2626', marginBottom: '6px' }} />
            <strong style={{ fontSize: '13px', color: '#991b1b' }}>Pengajuan Penghapusan Item</strong>
            <span style={{ fontSize: '12px', color: '#b91c1c' }}>Item ini akan dihapus dari website publik setelah disetujui.</span>
          </div>
        );
      }
      return (
        <div className="empty-value-box">
          <FileText size={18} className="empty-icon" />
          <span>Value cleared (Empty)</span>
        </div>
      );
    }

    // 1. Direct Video
    const fLower = (fieldId || '').toLowerCase();
    const isVideoField = fLower.includes('video');
    if (isVideoField && typeof parsed === 'string') {
      const videoSrc = parsed.startsWith('http') || parsed.startsWith('/')
        ? parsed
        : `/api/uploads/videos/${parsed}`;
      return (
        <div className="preview-video-container">
          <video src={videoSrc} controls className="preview-video-player" />
          <div className="preview-media-caption">
            <VideoIcon size={14} />
            <span>Video File: {parsed}</span>
          </div>
        </div>
      );
    }

    // 2. Direct Image (string URL or media ID) - only for genuine image fields or formats
    const isImageField = fLower.includes('image') || fLower.includes('photo') || fLower.includes('thumb') || fLower.includes('pic') || fLower.includes('logo') || fLower.includes('banner') || fLower.includes('cover') || fLower.includes('media_id') || fLower.includes('mediaid') || fLower.includes('gallery');
    const isKnownImageFormat = typeof parsed === 'string' && (parsed.startsWith('data:image/') || !!parsed.match(/\.(jpg|jpeg|png|webp|svg|gif|avif)$/i));

    if ((isImageField || isKnownImageFormat) && (typeof parsed === 'string' || typeof parsed === 'number')) {
      const resolvedUrl = resolveImage(parsed);
      const isImgDifferent = isOtherEmpty || (otherParsed !== null && otherParsed !== undefined && String(parsed) !== String(otherParsed));

      return (
        <SafeImagePreview
          src={resolvedUrl}
          rawMediaRef={String(parsed)}
          alt="Field Media"
          isBefore={isBefore}
          isImgDifferent={isImgDifferent}
          onOpenLightbox={(src, title) => setLightboxImage({ src, title })}
        />
      );
    }

    // 3. Array of items (e.g. highlights, schedule, tracks)
    if (Array.isArray(parsed)) {
      if (parsed.length === 0) {
        return <div className="empty-value-box">Empty list (0 items)</div>;
      }
      return (
        <div className="preview-array-list">
          <div className="array-count-badge">Total: {parsed.length} items</div>
          {parsed.map((item, idx) => {
            if (typeof item === 'object' && item !== null) {
              const itemImg = item.image || item.photo || item.thumbnail || item.logo || item.media_id || item.mediaId || (Array.isArray(item.images) ? item.images[0] : null);
              const resolvedItemImg = resolveImage(itemImg);
              const hasLabel = item.label !== undefined || item.key !== undefined;
              const hasValue = item.value !== undefined || item.val !== undefined;
              const hasLabelValue = hasLabel && hasValue;
              const itemLabel = item.label || item.key;
              const itemValue = item.value !== undefined ? item.value : item.val;
              const itemHeading = item.title || item.name || item.headline || item.heading || (hasLabel ? itemLabel : null) || (item.year ? `Tahun ${item.year}` : `Item ${idx + 1}`);
              const itemDetail = item.description || item.desc || item.detail || item.text || item.paragraph || item.bio;

              return (
                <div key={idx} className="preview-card-item">
                  <div className="card-item-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span className="card-idx-tag">#{idx + 1}</span>
                      <strong className="card-item-title">{itemHeading}</strong>
                    </div>
                    {hasLabelValue && (
                      <span style={{ 
                        fontWeight: '700', 
                        fontSize: '12px', 
                        color: isBefore ? '#7f1d1d' : '#047857',
                        background: isBefore ? '#fecaca' : '#a7f3d0',
                        padding: '2px 8px',
                        borderRadius: '4px'
                      }}>
                        {String(itemValue)}
                      </span>
                    )}
                    {item.year && !hasLabelValue && (
                      <span style={{ 
                        fontWeight: '700', 
                        fontSize: '11px', 
                        color: isBefore ? '#7f1d1d' : '#047857',
                        background: isBefore ? '#fee2e2' : '#dcfce7',
                        padding: '2px 6px',
                        borderRadius: '4px'
                      }}>
                        Tahun {String(item.year)}
                      </span>
                    )}
                  </div>
                  {resolvedItemImg && (
                    <img 
                      src={resolvedItemImg} 
                      alt="Thumbnail" 
                      className="card-item-thumb" 
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  {item.subtitle && <div className="card-item-sub">{item.subtitle}</div>}
                  {itemDetail && <div className="card-item-desc">{itemDetail}</div>}
                  {item.link && renderLinkDestinationPreview(item.link, false, isBefore)}
                </div>
              );
            }

            // Check if string item in array is an image
            const itemStr = String(item || '').trim();
            const isItemImage = isImageField || itemStr.startsWith('data:image/') || !!itemStr.match(/\.(jpg|jpeg|png|webp|svg|gif|avif)($|\?)/i) || itemStr.includes('/uploads/');
            const resolvedItemImg = isItemImage ? resolveImage(itemStr) : null;

            if (resolvedItemImg) {
              const otherArr = Array.isArray(otherParsed) ? otherParsed : [];
              const otherItemStr = String(otherArr[idx] || '').trim();
              const isImgDiff = isOtherEmpty || otherArr[idx] === undefined || itemStr !== otherItemStr;

              return (
                <div key={idx} className="preview-card-item" style={{ marginBottom: '12px' }}>
                  <div className="card-item-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span className="card-idx-tag">Foto #{idx + 1}</span>
                    </div>
                    <span style={{ fontSize: '11px', color: '#64748b', wordBreak: 'break-all', maxWidth: '70%', textAlign: 'right' }}>
                      {itemStr.split('/').pop()}
                    </span>
                  </div>
                  <SafeImagePreview
                    src={resolvedItemImg}
                    rawMediaRef={itemStr}
                    alt={`Preview Foto #${idx + 1}`}
                    isBefore={isBefore}
                    isImgDifferent={isImgDiff}
                    onOpenLightbox={(src, title) => setLightboxImage({ src, title })}
                  />
                </div>
              );
            }

            return (
              <div key={idx} className="preview-simple-pill">
                #{idx + 1}: {String(item)}
              </div>
            );
          })}
        </div>
      );
    }

    // 4. Structured Object (e.g. Card, Slide, House, Event)
    if (typeof parsed === 'object') {
      const objImg = parsed.image || parsed.photo || parsed.thumbnail || parsed.logo || parsed.media_id || parsed.mediaId || parsed.url;
      const resolvedObjImg = resolveImage(objImg);
      const otherImg = otherParsed && typeof otherParsed === 'object' ? (otherParsed.image || otherParsed.photo || otherParsed.thumbnail || otherParsed.logo || otherParsed.media_id || otherParsed.mediaId || otherParsed.url) : null;
      const isImgDifferent = isOtherEmpty || (otherParsed !== null && otherParsed !== undefined && String(objImg || '') !== String(otherImg || ''));

      const isCardOrItem = fLower.includes('item') || fLower.includes('slide') || fLower.includes('card') || fLower.includes('highlight') || fLower.includes('event') || fLower.includes('news') || fLower.includes('step') || fLower.includes('level') || fLower.includes('program');

      return (
        <div className="preview-object-container">
          {resolvedObjImg ? (
            <SafeImagePreview
              src={resolvedObjImg}
              rawMediaRef={String(objImg)}
              alt="Slide / Item Media"
              isBefore={isBefore}
              isImgDifferent={isImgDifferent}
              onOpenLightbox={(src, title) => setLightboxImage({ src, title })}
            />
          ) : objImg ? (
            <div 
              className={`preview-image-container mb-3 ${isImgDifferent ? (isBefore ? 'field-card-diff-before' : 'field-card-diff-after') : ''}`}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                borderRadius: '10px',
                padding: '16px',
                border: isImgDifferent ? (isBefore ? '2px solid #ef4444' : '2px solid #10b981') : '1.5px dashed #cbd5e1',
                backgroundColor: isImgDifferent ? (isBefore ? '#fef2f2' : '#f0fdf4') : '#f8fafc',
                textAlign: 'center'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: isImgDifferent ? (isBefore ? '#b91c1c' : '#15803d') : '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {isBefore ? 'Current Published Photo' : (isImgDifferent ? 'Proposed New Photo' : 'Slide Photo')}
                </span>
                {isImgDifferent && (
                  <span style={{
                    fontSize: '10px',
                    padding: '2px 8px',
                    borderRadius: '999px',
                    backgroundColor: isBefore ? '#fee2e2' : '#dcfce7',
                    color: isBefore ? '#b91c1c' : '#15803d',
                    fontWeight: '800',
                    letterSpacing: '0.04em'
                  }}>
                    {isBefore ? 'ORIGINAL' : 'NEW PHOTO'}
                  </span>
                )}
              </div>
              <ImageIcon size={32} style={{ color: isImgDifferent ? '#10b981' : '#94a3b8', margin: '0 auto 8px' }} />
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>
                Photo Reference: {String(objImg)}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                {/^\d+$/.test(String(objImg)) ? `Media ID #${objImg} (Registered in Media Library)` : String(objImg)}
              </div>
            </div>
          ) : isCardOrItem ? (
            <div style={{
              padding: '12px 14px',
              borderRadius: '8px',
              backgroundColor: '#fffbeb',
              border: '1.5px dashed #f59e0b',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '14px',
              color: '#92400e',
              fontSize: '12.5px',
              fontWeight: '600'
            }}>
              <AlertTriangle size={18} style={{ color: '#d97706', flexShrink: 0 }} />
              <span>Tidak ada foto terlampir pada item ini (Hanya teks judul & ringkasan).</span>
            </div>
          ) : null}

          <div className="preview-kv-grid" style={{ display: 'flex', flexDirection: 'column', gap: '10px', background: 'transparent', padding: 0, border: 'none' }}>
            {Object.entries(parsed).map(([k, v]) => {
              // Hide media keys (rendered above as image) and internal technical ID keys
              if (k === 'image' || k === 'photo' || k === 'thumbnail' || k === 'logo' || k === 'media_id' || k === 'mediaId' || k === 'id' || k === '_id' || k === 'key') {
                return null;
              }
              const meta = getFieldMeta(k);
              const FieldIcon = meta.icon;
              const otherVal = otherParsed && typeof otherParsed === 'object' ? otherParsed[k] : undefined;
              const isDifferent = isOtherEmpty || (otherParsed !== null && otherParsed !== undefined && typeof otherParsed === 'object' && JSON.stringify(v) !== JSON.stringify(otherVal));
              const isValNull = v === null || v === undefined || v === 'null';
              const valStr = isValNull ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v));
              const tagLabel = isBefore ? 'ORIGINAL' : (isOtherEmpty ? 'NEW' : 'UPDATED');

              return (
                <div 
                  key={k} 
                  className={`preview-field-card ${isDifferent ? (isBefore ? 'field-card-diff-before' : 'field-card-diff-after') : ''}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    backgroundColor: isDifferent ? (isBefore ? '#fef2f2' : '#f0fdf4') : '#ffffff',
                    border: `1.5px solid ${isDifferent ? (isBefore ? '#fca5a5' : '#86efac') : '#e2e8f0'}`,
                    borderLeft: `4px solid ${isDifferent ? (isBefore ? '#ef4444' : '#10b981') : '#94a3b8'}`,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FieldIcon size={12} style={{ color: isDifferent ? (isBefore ? '#b91c1c' : '#15803d') : '#64748b' }} />
                      <span style={{ 
                        fontSize: '11px', 
                        fontWeight: '700', 
                        textTransform: 'uppercase', 
                        letterSpacing: '0.04em',
                        color: isDifferent ? (isBefore ? '#991b1b' : '#166534') : '#64748b' 
                      }}>
                        {meta.label}
                      </span>
                    </div>

                    {isDifferent && (
                      <span style={{ 
                        fontSize: '10px', 
                        padding: '2px 8px', 
                        borderRadius: '999px', 
                        backgroundColor: isBefore ? '#fee2e2' : '#dcfce7', 
                        color: isBefore ? '#b91c1c' : '#15803d',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em'
                      }}>
                        {tagLabel}
                      </span>
                    )}
                  </div>

                  {meta.isLink ? (
                    renderLinkDestinationPreview(v, isDifferent, isBefore)
                  ) : Array.isArray(v) ? (
                    v.length === 0 ? (
                      <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '13px', padding: '4px 0' }}>
                        (Tidak ada poin / Kosong)
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                        {v.map((item, pIdx) => {
                          const otherArr = Array.isArray(otherVal) ? otherVal : [];
                          const otherItem = otherArr[pIdx];
                          const isItemDiff = isOtherEmpty || !otherItem || JSON.stringify(item) !== JSON.stringify(otherItem);
                          const isCardDiff = isDifferent && isItemDiff;

                          if (typeof item === 'object' && item !== null) {
                            const hasLabel = item.label !== undefined || item.key !== undefined;
                            const hasValue = item.value !== undefined || item.val !== undefined;
                            const hasLabelValue = hasLabel && hasValue;
                            const itemLabel = item.label || item.key;
                            const itemValue = item.value !== undefined ? item.value : item.val;
                            const itemHeading = item.title || item.name || item.headline || item.heading || (hasLabel ? itemLabel : null) || (item.year ? `Tahun ${item.year}` : null);
                            const itemDetail = item.description || item.desc || item.detail || item.text || item.paragraph || item.bio;
                            const itemImg = item.image || item.photo || item.thumbnail || item.logo || item.media_id || item.mediaId || (Array.isArray(item.images) ? item.images[0] : null);
                            const resolvedItemImg = itemImg ? resolveImage(itemImg) : null;

                            const knownKeys = ['label', 'key', 'value', 'val', 'title', 'name', 'headline', 'heading', 'year', 'description', 'desc', 'detail', 'text', 'paragraph', 'bio', 'image', 'photo', 'thumbnail', 'logo', 'media_id', 'mediaId', 'link', 'id', '_id'];
                            const extraEntries = Object.entries(item).filter(([k2]) => !knownKeys.includes(k2));

                            return (
                              <div 
                                key={pIdx} 
                                style={{ 
                                  padding: '9px 12px', 
                                  borderRadius: '6px', 
                                  backgroundColor: isCardDiff ? (isBefore ? '#fee2e2' : '#dcfce7') : '#f8fafc',
                                  border: `1px solid ${isCardDiff ? (isBefore ? '#fca5a5' : '#86efac') : '#e2e8f0'}` 
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                                  <div style={{ fontWeight: '700', fontSize: '13px', color: isCardDiff ? (isBefore ? '#991b1b' : '#166534') : '#1e293b' }}>
                                    #{pIdx + 1}. {itemHeading || `Poin ${pIdx + 1}`}
                                  </div>
                                  {hasLabelValue && (
                                    <span style={{ 
                                      fontWeight: '700', 
                                      fontSize: '12px', 
                                      color: isCardDiff ? (isBefore ? '#7f1d1d' : '#047857') : '#1d4ed8',
                                      background: isCardDiff ? (isBefore ? '#fecaca' : '#a7f3d0') : '#eff6ff',
                                      padding: '2px 8px',
                                      borderRadius: '4px',
                                      border: `1px solid ${isCardDiff ? (isBefore ? '#fca5a5' : '#6ee7b7') : '#bfdbfe'}`
                                    }}>
                                      {String(itemValue)}
                                    </span>
                                  )}
                                  {item.year && !hasLabelValue && (
                                    <span style={{ 
                                      fontWeight: '700', 
                                      fontSize: '11.5px', 
                                      color: isCardDiff ? (isBefore ? '#7f1d1d' : '#047857') : '#475569',
                                      background: isCardDiff ? (isBefore ? '#fecaca' : '#a7f3d0') : '#e2e8f0',
                                      padding: '2px 7px',
                                      borderRadius: '4px'
                                    }}>
                                      Tahun {String(item.year)}
                                    </span>
                                  )}
                                </div>
                                {resolvedItemImg && (
                                  <img 
                                    src={resolvedItemImg} 
                                    alt="Item thumbnail" 
                                    style={{ width: '100%', maxHeight: '130px', objectFit: 'cover', borderRadius: '4px', marginTop: '6px' }} 
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )}
                                {itemDetail && (
                                  <div style={{ fontSize: '12.5px', color: isCardDiff ? (isBefore ? '#7f1d1d' : '#14532d') : '#475569', lineHeight: '1.4', marginTop: '4px' }}>
                                    {itemDetail}
                                  </div>
                                )}
                                {extraEntries.length > 0 && (
                                  <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    {extraEntries.map(([k2, v2]) => (
                                      <div key={k2} style={{ fontSize: '11.5px', color: '#64748b' }}>
                                        <strong style={{ textTransform: 'capitalize' }}>{k2.replace(/_/g, ' ')}:</strong>{' '}
                                        {Array.isArray(v2) ? (
                                          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                                            {v2.map((pt, ptIdx) => (
                                              <li key={ptIdx} style={{ fontSize: '12px', color: '#475569', marginBottom: '2px' }}>
                                                {String(pt)}
                                              </li>
                                            ))}
                                          </ul>
                                        ) : typeof v2 === 'object' ? JSON.stringify(v2) : String(v2)}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {item.link && (
                                  <div style={{ marginTop: '6px' }}>
                                    {renderLinkDestinationPreview(item.link, isCardDiff, isBefore)}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          const itemStr = String(item || '').trim();
                          const isItemImage = (k || '').toLowerCase().includes('image') || (k || '').toLowerCase().includes('photo') || (k || '').toLowerCase().includes('gallery') || itemStr.startsWith('data:image/') || !!itemStr.match(/\.(jpg|jpeg|png|webp|svg|gif|avif)($|\?)/i) || itemStr.includes('/uploads/');
                          const resolvedImg = isItemImage ? resolveImage(itemStr) : null;
                          if (resolvedImg) {
                            return (
                              <div key={pIdx} style={{ marginTop: '6px', marginBottom: '8px' }}>
                                <SafeImagePreview
                                  src={resolvedImg}
                                  rawMediaRef={itemStr}
                                  alt={`Foto #${pIdx + 1}`}
                                  isBefore={isBefore}
                                  isImgDifferent={isCardDiff}
                                  onOpenLightbox={(src, title) => setLightboxImage({ src, title })}
                                />
                              </div>
                            );
                          }
                          return (
                            <div 
                              key={pIdx} 
                              style={{ 
                                fontSize: '13px', 
                                color: isCardDiff ? (isBefore ? '#7f1d1d' : '#064e3b') : '#0f172a',
                                padding: '3px 8px',
                                borderRadius: '4px',
                                backgroundColor: isCardDiff ? (isBefore ? '#fee2e2' : '#dcfce7') : 'transparent'
                              }}
                            >
                              • {String(item)}
                            </div>
                          );
                        })}
                      </div>
                    )
                  ) : (
                    <div style={{ 
                      fontSize: k === 'title' ? '15px' : '13.5px', 
                      fontWeight: k === 'title' ? '700' : '500', 
                      lineHeight: '1.5',
                      color: isDifferent ? (isBefore ? '#7f1d1d' : '#064e3b') : '#0f172a',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {valStr || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>(Empty)</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <details className="raw-json-details">
            <summary>View Raw Data</summary>
            <pre className="raw-json-code">{JSON.stringify(parsed, null, 2)}</pre>
          </details>
        </div>
      );
    }

    // 5. Google Maps Embed Preview
    if (fLower.includes('map_embed') && typeof parsed === 'string' && parsed.trim().length > 0) {
      const cleanedMapUrl = cleanGoogleMapsEmbedUrl(parsed);
      return (
        <div className="preview-map-container" style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #cbd5e1' }}>
          <iframe
            title={isBefore ? "Map Sebelum" : "Map Sesudah"}
            src={cleanedMapUrl}
            style={{ width: '100%', height: '200px', border: 0, display: 'block' }}
            loading="lazy"
          />
          <div style={{ padding: '8px 12px', background: '#f8fafc', fontSize: '12px', color: '#64748b', wordBreak: 'break-all' }}>
            <strong>URL Embed:</strong> {cleanedMapUrl}
          </div>
        </div>
      );
    }

    // 6. Plain Text / Paragraph / String
    const isTextDifferent = otherParsed !== null && otherParsed !== undefined && String(parsed).trim() !== String(otherParsed).trim();
    const isSingleLinkField = fLower.includes('link') || fLower.includes('url') || fLower.includes('href') || (typeof parsed === 'string' && (parsed.startsWith('http://') || parsed.startsWith('https://') || parsed.startsWith('/')));

    if (isSingleLinkField && typeof parsed === 'string') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ 
              fontSize: '11px', 
              fontWeight: '700', 
              textTransform: 'uppercase', 
              letterSpacing: '0.05em',
              color: isTextDifferent ? (isBefore ? '#991b1b' : '#166534') : '#64748b' 
            }}>
              {isBefore ? 'Live Published Link Destination' : (isTextDifferent ? 'Proposed Link Destination' : 'Link Destination')}
            </span>
            {isTextDifferent && (
              <span style={{ 
                fontSize: '11px', 
                padding: '3px 8px', 
                borderRadius: '999px', 
                backgroundColor: isBefore ? '#fee2e2' : '#dcfce7', 
                color: isBefore ? '#b91c1c' : '#15803d', 
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
              }}>
                {isBefore ? 'To Be Replaced' : 'New Content'}
              </span>
            )}
          </div>
          {renderLinkDestinationPreview(parsed, isTextDifferent, isBefore)}
        </div>
      );
    }

    return (
      <div 
        className="preview-text-block"
        style={{
          backgroundColor: isTextDifferent 
            ? (isBefore ? '#fff5f5' : '#f0fdf4') 
            : '#f8fafc',
          border: `1.5px solid ${isTextDifferent ? (isBefore ? '#fecaca' : '#bbf7d0') : '#e2e8f0'}`,
          borderLeft: `5px solid ${isTextDifferent ? (isBefore ? '#ef4444' : '#10b981') : '#94a3b8'}`,
          padding: '16px 18px',
          borderRadius: '10px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ 
            fontSize: '11px', 
            fontWeight: '700', 
            textTransform: 'uppercase', 
            letterSpacing: '0.05em',
            color: isTextDifferent ? (isBefore ? '#991b1b' : '#166534') : '#64748b' 
          }}>
            {isBefore ? 'Live Published Text' : (isTextDifferent ? 'Proposed Revision' : 'Current Value')}
          </span>
          {isTextDifferent && (
            <span style={{ 
              fontSize: '11px', 
              padding: '3px 8px', 
              borderRadius: '999px', 
              backgroundColor: isBefore ? '#fee2e2' : '#dcfce7', 
              color: isBefore ? '#b91c1c' : '#15803d',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.03em'
            }}>
              {isBefore ? 'To Be Replaced' : 'New Content'}
            </span>
          )}
        </div>
        <p className="preview-text-content" style={{ 
          color: isTextDifferent ? (isBefore ? '#7f1d1d' : '#064e3b') : '#1e293b', 
          fontWeight: isTextDifferent ? '600' : '500', 
          fontSize: '15px',
          lineHeight: '1.6',
          margin: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          {String(parsed)}
        </p>
      </div>
    );
  };

  /**
   * TAB 1: EXECUTIVE PRINCIPAL DASHBOARD OVERVIEW
   * Uses the exact .wa-* design system from DashboardOverview.css for visual consistency
   */
  const renderDashboardOverview = () => {
    const pendingMenuItems = contentSubmissions.filter(item => item.status === 'pending');
    const pendingTestimonialItems = testimonialSubmissions.filter(item => item.status === 'pending');

    const totalReviewed = (statusCounts.approved || 0) + (statusCounts.rejected || 0);
    const approvalRate = totalReviewed > 0 ? Math.round(((statusCounts.approved || 0) / totalReviewed) * 100) : 100;

    const activePublishedTestimonialsCount = Object.values(testimonialsMap?.byId || {}).filter(
      t => Number(t.is_active) === 1 || t.status === 'approved'
    ).length;

    const quickAccessCards = [
      {
        key: 'homepage',
        title: 'Homepage',
        desc: 'Hero banner video, quick links, highlights kegiatan, berita & countdown acara.',
        icon: Home,
        iconClass: 'home',
        pending: menuStats.homepage || 0,
        approved: menuStats.approvedByMenu?.homepage || 0,
        isTestimonial: false
      },
      {
        key: 'about-us',
        title: 'About Us',
        desc: 'Pesan Principal, Visi-Misi, Sejarah, Nilai Inti, dan Jajaran Pimpinan Sekolah.',
        icon: Landmark,
        iconClass: 'about',
        pending: menuStats['about-us'] || 0,
        approved: menuStats.approvedByMenu?.['about-us'] || 0,
        isTestimonial: false
      },
      {
        key: 'education-program',
        title: 'Education Program',
        desc: 'Kurikulum Preschool, Kindergarten, Primary, Secondary, dan metode pembelajaran.',
        icon: GraduationCap,
        iconClass: 'edu',
        pending: menuStats['education-program'] || 0,
        approved: menuStats.approvedByMenu?.['education-program'] || 0,
        isTestimonial: false
      },
      {
        key: 'admissions',
        title: 'Admissions',
        desc: 'Alur pendaftaran siswa baru, jalur masuk, jadwal gelombang, dan persyaratan.',
        icon: FileEdit,
        iconClass: 'adm',
        pending: menuStats.admissions || 0,
        approved: menuStats.approvedByMenu?.admissions || 0,
        isTestimonial: false
      },
      {
        key: 'student-life',
        title: 'Student Life',
        desc: 'Ekstrakurikuler, house system, pembentukan karakter, dan galeri aktivitas siswa.',
        icon: Activity,
        iconClass: 'student',
        pending: menuStats['student-life'] || 0,
        approved: menuStats.approvedByMenu?.['student-life'] || 0,
        isTestimonial: false
      },
      {
        key: 'testimonials',
        title: 'Testimonials (Dedicated)',
        desc: 'Menu terpisah untuk memoderasi publikasi (ON/OFF) dan penghapusan ulasan testimoni.',
        icon: Quote,
        iconClass: 'test',
        pending: testimonialStatusCounts.pending || 0,
        approved: testimonialStatusCounts.approved || 0,
        isTestimonial: true
      }
    ];

    const moduleDirectory = [
      { key: 'homepage', name: 'Homepage', desc: 'Hero banner, statistik, keunggulan & program utama', sectionsCount: 5, isTestimonial: false },
      { key: 'about-us', name: 'About Us', desc: 'Pesan Kepala Sekolah, visi-misi, nilai inti & sejarah', sectionsCount: 6, isTestimonial: false },
      { key: 'education-program', name: 'Education Program', desc: 'Kurikulum PG-TK, SD, SMP, SMA & metode belajar', sectionsCount: 7, isTestimonial: false },
      { key: 'admissions', name: 'Admissions', desc: 'Alur pendaftaran, persyaratan & jadwal gelombang', sectionsCount: 7, isTestimonial: false },
      { key: 'student-life', name: 'Student Life', desc: 'Kegiatan siswa, ekstrakurikuler & house system', sectionsCount: 7, isTestimonial: false },
      { key: 'facilities', name: 'Facilities', desc: 'Fasilitas laboratorium, perpustakaan & keamanan kampus', sectionsCount: 6, isTestimonial: false },
      { key: 'news-updates', name: 'News & Updates', desc: 'Berita sekolah, siaran pers, artikel & pengumuman', sectionsCount: 3, isTestimonial: false },
      { key: 'parents', name: 'Parents', desc: 'Komunitas orang tua (PaLC), buku panduan & informasi', sectionsCount: 5, isTestimonial: false },
      { key: 'careers', name: 'Careers', desc: 'Filosofi perekrutan, lowongan kerja guru & staf', sectionsCount: 4, isTestimonial: false },
      { key: 'contact-us', name: 'Contact Us', desc: 'Alamat kampus, kontak WhatsApp/Telepon, FAQ & kemitraan', sectionsCount: 5, isTestimonial: false },
      { key: 'footer', name: 'Footer', desc: 'Tautan cepat, hak cipta & sosial media resmi sekolah', sectionsCount: 2, isTestimonial: false },
      { key: 'testimonials', name: 'Testimonials (Menu Terpisah)', desc: 'Moderasi ulasan orang tua, siswa & alumni secara terpisah', sectionsCount: 1, isTestimonial: true }
    ];

    return (
      <div className="wa-overview-container">
        {/* 1. WELCOME BANNER HEADER */}
        <section className="wa-welcome-banner">
          <div className="wa-welcome-left">
            <div className="wa-welcome-role-badge">
              <ShieldCheck size={14} />
              <span>Principal &amp; Chief Content Approver</span>
            </div>
            <h1 className="wa-welcome-title">
              Selamat Datang, {user?.full_name || user?.name || 'Principal'}
            </h1>
            <p className="wa-welcome-subtitle">
              Portal Otorisasi Publikasi Konten Resmi Permata Harapan Secondary School. Antrean perubahan menu halaman website dan moderasi ulasan testimoni kini dipisahkan agar lebih mudah ditinjau.
            </p>
          </div>
          <div className="wa-welcome-actions">
            <button
              type="button"
              className="wa-btn-refresh"
              onClick={() => {
                loadSubmissions('all');
                loadTestimonialsMap();
                loadPendingFeeLevels();
              }}
              disabled={loading}
              title="Muat ulang data antrean persetujuan"
            >
              <RefreshCw size={15} className={loading ? 'spin' : ''} />
              <span>{loading ? 'Memperbarui...' : 'Refresh Data'}</span>
            </button>

            <a
              href="https://permataharapansecondary.sch.id"
              target="_blank"
              rel="noopener noreferrer"
              className="wa-btn-live-site"
              title="Buka website publik di tab baru"
            >
              <ExternalLink size={15} />
              <span>Lihat Website Publik</span>
            </a>
          </div>
        </section>

        {/* 2. CONDITIONAL ACTION ALERTS */}
        {contentStatusCounts.pending > 0 && (
          <section className="wa-alert-banner info">
            <div className="wa-alert-icon-box">
              <ClipboardList size={20} />
            </div>
            <div className="wa-alert-text-wrap">
              <div className="wa-alert-heading">
                <span className="wa-alert-pill info">Antrean Menu Website</span>
                <strong>{contentStatusCounts.pending} Pengajuan Perubahan Menu Website Menunggu Persetujuan</strong>
              </div>
              <p className="wa-alert-desc">
                Terdapat {contentStatusCounts.pending} pengajuan perubahan konten pada menu halaman website dari Website Admin yang menunggu verifikasi Anda sebelum ditayangkan ke publik.
              </p>
            </div>
            <button
              type="button"
              className="wa-alert-action-btn info"
              onClick={() => {
                setSelectedStatusFilter('pending');
                setSelectedMenuFilter('all');
                setActiveTab('approvals');
              }}
            >
              <span>Tinjau Submissions ({contentStatusCounts.pending})</span>
              <ArrowRight size={14} />
            </button>
          </section>
        )}

        {testimonialStatusCounts.pending > 0 && (
          <section className="wa-alert-banner warning">
            <div className="wa-alert-icon-box">
              <Quote size={20} />
            </div>
            <div className="wa-alert-text-wrap">
              <div className="wa-alert-heading">
                <span className="wa-alert-pill warning">Moderasi Testimoni</span>
                <strong>{testimonialStatusCounts.pending} Pengajuan Ulasan Testimoni Menunggu Moderasi Principal</strong>
              </div>
              <p className="wa-alert-desc">
                Terdapat {testimonialStatusCounts.pending} permintaan aktivasi (Publish ON), penonaktifan (Unpublish OFF), atau penghapusan testimoni di menu khusus Testimonials yang menunggu keputusan Anda.
              </p>
            </div>
            <button
              type="button"
              className="wa-alert-action-btn warning"
              onClick={() => {
                setTestimonialStatusFilter('pending');
                setTestimonialActionFilter('all');
                setActiveTab('testimonial-approvals');
              }}
            >
              <span>Tinjau Testimonials ({testimonialStatusCounts.pending})</span>
              <ArrowRight size={14} />
            </button>
          </section>
        )}

        {/* 2B. ADMISSIONS CONFIG & FEE STRUCTURE PENDING PRINCIPAL APPROVAL (NOTIFICATION ONLY) */}
        {pendingFeeLevels.length > 0 && (
          <section className="wa-alert-banner warning" style={{ borderLeft: '4px solid #f59e0b' }}>
            <div className="wa-alert-content">
              <span className="wa-alert-tag" style={{ background: '#fef3c7', color: '#92400e' }}>
                Otorisasi Konfigurasi & Tarif PPDB (Admissions)
              </span>
              <h3>
                {pendingFeeLevels.length} Pengajuan Konfigurasi & Tarif Biaya PPDB Menunggu Persetujuan
              </h3>
              <p>
                Terdapat pengajuan dari Admin Admissions (Tarif Biaya Jenjang, Periode & Kuota, Jadwal Tes Offline, atau Kode Promo) yang memerlukan persetujuan Kepala Sekolah sebelum aktif di portal orang tua.
              </p>
            </div>
            <button
              type="button"
              className="wa-alert-cta-btn"
              style={{ background: '#f59e0b', color: '#ffffff' }}
              onClick={() => setActiveTab('admissions-fee-approvals')}
            >
              <span>Tinjau Persetujuan PPDB ({pendingFeeLevels.length})</span>
              <ArrowRight size={14} />
            </button>
          </section>
        )}

        {/* 3. EXECUTIVE STATS METRICS GRID (5 KPI CARDS) */}
        <section className="wa-stats-grid">
          {/* Card 1: Submissions Menu Pending */}
          <div
            className="wa-stat-card clickable"
            onClick={() => {
              setSelectedStatusFilter('pending');
              setSelectedMenuFilter('all');
              setActiveTab('approvals');
            }}
            title="Buka Antrean Submissions Menu Website"
          >
            <div className="wa-stat-icon-wrap amber">
              <ClipboardList size={22} />
            </div>
            <div className="wa-stat-body">
              <div className="wa-stat-label-row">
                <span className="wa-stat-label">Menu Pending</span>
                <ChevronRight size={14} className="wa-stat-arrow" />
              </div>
              <div className="wa-stat-number">{contentStatusCounts.pending || 0}</div>
              <span className="wa-stat-hint">Antrean 11 menu utama website</span>
            </div>
          </div>

          {/* Card 2: Testimonials Pending */}
          <div
            className="wa-stat-card clickable"
            onClick={() => {
              setTestimonialStatusFilter('pending');
              setTestimonialActionFilter('all');
              setActiveTab('testimonial-approvals');
            }}
            title="Buka Antrean Khusus Testimonials"
          >
            <div className="wa-stat-icon-wrap pink">
              <Quote size={22} />
            </div>
            <div className="wa-stat-body">
              <div className="wa-stat-label-row">
                <span className="wa-stat-label">Testimonial Pending</span>
                <ChevronRight size={14} className="wa-stat-arrow" />
              </div>
              <div className="wa-stat-number">{testimonialStatusCounts.pending || 0}</div>
              <span className="wa-stat-hint">Moderasi ulasan testimoni</span>
            </div>
          </div>

          {/* Card 3: Total Approved */}
          <div
            className="wa-stat-card clickable"
            onClick={() => {
              setSelectedStatusFilter('approved');
              setActiveTab('approvals');
            }}
            title="Lihat Riwayat Pengajuan yang Disetujui"
          >
            <div className="wa-stat-icon-wrap green">
              <CheckCircle2 size={22} />
            </div>
            <div className="wa-stat-body">
              <div className="wa-stat-label-row">
                <span className="wa-stat-label">Total Disetujui</span>
                <ChevronRight size={14} className="wa-stat-arrow" />
              </div>
              <div className="wa-stat-number">{statusCounts.approved || 0}</div>
              <span className="wa-stat-hint">
                {contentStatusCounts.approved || 0} Menu &bull; {testimonialStatusCounts.approved || 0} Testimoni
              </span>
            </div>
          </div>

          {/* Card 4: Total Rejected */}
          <div
            className="wa-stat-card clickable"
            onClick={() => {
              setSelectedStatusFilter('rejected');
              setActiveTab('approvals');
            }}
            title="Lihat Pengajuan yang Ditolak"
          >
            <div className="wa-stat-icon-wrap slate">
              <XCircle size={22} />
            </div>
            <div className="wa-stat-body">
              <div className="wa-stat-label-row">
                <span className="wa-stat-label">Ditolak / Revisi</span>
                <ChevronRight size={14} className="wa-stat-arrow" />
              </div>
              <div className="wa-stat-number">{statusCounts.rejected || 0}</div>
              <span className="wa-stat-hint">Dikembalikan dengan catatan</span>
            </div>
          </div>

          {/* Card 5: Approval Rate */}
          <div
            className="wa-stat-card clickable"
            onClick={() => {
              setTestimonialStatusFilter('all');
              setActiveTab('testimonial-approvals');
            }}
            title="Persentase Persetujuan Publikasi"
          >
            <div className="wa-stat-icon-wrap purple">
              <FileCheck size={22} />
            </div>
            <div className="wa-stat-body">
              <div className="wa-stat-label-row">
                <span className="wa-stat-label">Approval Rate</span>
                <ChevronRight size={14} className="wa-stat-arrow" />
              </div>
              <div className="wa-stat-number">{approvalRate}%</div>
              <span className="wa-stat-hint">{activePublishedTestimonialsCount} testimoni publik aktif</span>
            </div>
          </div>
        </section>

        {/* 4. TWO-COLUMN SEPARATED PENDING QUEUES (MENU SUBMISSIONS VS TESTIMONIALS) */}
        <div className="pr-two-col-grid">
          {/* LEFT CARD: PENDING MENU SUBMISSIONS */}
          <section className="wa-section-card pr-queue-panel">
            <div className="pr-section-header-row">
              <div>
                <div className="pr-section-title-group">
                  <h2 className="wa-section-title">Antrean Submissions Menu Website</h2>
                  <span className="pr-queue-count-pill pr-pill-blue">
                    {pendingMenuItems.length} Pending
                  </span>
                </div>
                <p className="wa-section-subtitle">
                  Pengajuan perubahan teks, foto, dan informasi dari 11 menu utama website
                </p>
              </div>
              <button
                type="button"
                className="wa-btn-table-edit"
                onClick={() => {
                  setSelectedStatusFilter('pending');
                  setSelectedMenuFilter('all');
                  setActiveTab('approvals');
                }}
              >
                <span>Buka Semua</span>
                <ArrowRight size={12} />
              </button>
            </div>

            {loading ? (
              <div className="pr-overview-empty-state">
                <RefreshCw size={24} className="spinning" />
                <p>Memuat antrean menu website...</p>
              </div>
            ) : pendingMenuItems.length === 0 ? (
              <div className="pr-overview-empty-state">
                <CheckCircle2 size={36} color="#10b981" />
                <h4>Semua Menu Website Tersinkronisasi</h4>
                <p>Tidak ada pengajuan perubahan menu website yang sedang menunggu review Principal.</p>
              </div>
            ) : (
              <div className="wa-table-responsive">
                <table className="wa-directory-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Menu</th>
                      <th>Bagian &amp; Field</th>
                      <th>Aksi</th>
                      <th className="text-right">Review</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingMenuItems.slice(0, 5).map(item => {
                      const menu = getMenuCategory(item.page, item.section_type, item.field_name, item.field_id);
                      const MenuIcon = menu.icon;
                      const sectionName = getSectionDisplayName(item.section_type, item.field_name, item.field_id);
                      const fieldDisplay = formatFieldDisplayName(item.field_name, item.field_id);
                      const op = getOperationInfo(item.field_id, item.old_value, item.new_value, item.field_name);
                      const OpIcon = op.icon;

                      return (
                        <tr key={item.id}>
                          <td>
                            <span className="id-badge">#{item.id}</span>
                          </td>
                          <td>
                            <span
                              className="menu-pill-mini"
                              style={{ background: menu.bg, color: menu.color, border: `1px solid ${menu.border}` }}
                            >
                              <MenuIcon size={12} />
                              <span>{menu.name}</span>
                            </span>
                          </td>
                          <td>
                            <div className="wa-dir-title" style={{ fontSize: '0.8125rem' }}>{sectionName}</div>
                            <div className="wa-td-desc" style={{ fontSize: '0.75rem' }}>{fieldDisplay}</div>
                          </td>
                          <td>
                            <span
                              className="operation-pill-badge"
                              style={{
                                backgroundColor: op.bg,
                                color: op.color,
                                border: `1px solid ${op.border}`
                              }}
                            >
                              <OpIcon size={11} />
                              <span>{op.iconText}</span>
                            </span>
                          </td>
                          <td className="text-right">
                            <button
                              type="button"
                              className="btn-action-review"
                              onClick={() => openReviewModal(item)}
                            >
                              <Eye size={13} />
                              <span>Review</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* RIGHT CARD: PENDING TESTIMONIALS SUBMISSIONS */}
          <section className="wa-section-card pr-queue-panel">
            <div className="pr-section-header-row">
              <div>
                <div className="pr-section-title-group">
                  <h2 className="wa-section-title">Antrean Submissions Testimonial</h2>
                  <span className="pr-queue-count-pill pr-pill-teal">
                    {pendingTestimonialItems.length} Pending
                  </span>
                </div>
                <p className="wa-section-subtitle">
                  Pengajuan aktivasi (Publish ON), penonaktifan (OFF), atau hapus ulasan testimoni
                </p>
              </div>
              <button
                type="button"
                className="wa-btn-table-edit"
                onClick={() => {
                  setTestimonialStatusFilter('pending');
                  setTestimonialActionFilter('all');
                  setActiveTab('testimonial-approvals');
                }}
              >
                <span>Buka Testimonials</span>
                <ArrowRight size={12} />
              </button>
            </div>

            {loading ? (
              <div className="pr-overview-empty-state">
                <RefreshCw size={24} className="spinning" />
                <p>Memuat antrean testimoni...</p>
              </div>
            ) : pendingTestimonialItems.length === 0 ? (
              <div className="pr-overview-empty-state">
                <CheckCircle2 size={36} color="#10b981" />
                <h4>Antrean Testimoni Bersih</h4>
                <p>Tidak ada pengajuan moderasi ulasan testimoni yang sedang menunggu keputusan Principal.</p>
              </div>
            ) : (
              <div className="wa-table-responsive">
                <table className="wa-directory-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Author &amp; Role</th>
                      <th>Kutipan Ulasan</th>
                      <th>Aksi</th>
                      <th className="text-right">Review</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingTestimonialItems.slice(0, 5).map(item => {
                      const tInfo = extractTestimonialInfo(item);
                      const op = getOperationInfo(item.field_id, item.old_value, item.new_value, item.field_name);
                      const OpIcon = op.icon;

                      return (
                        <tr key={item.id}>
                          <td>
                            <span className="id-badge">#{item.id}</span>
                          </td>
                          <td>
                            <div className="wa-dir-title" style={{ fontSize: '0.8125rem' }}>{tInfo.authorName}</div>
                            <div className="wa-td-desc" style={{ fontSize: '0.72rem' }}>{tInfo.authorRole}</div>
                          </td>
                          <td>
                            <div className="pr-mini-quote-preview" title={tInfo.quoteText}>
                              &ldquo;{tInfo.quoteText}&rdquo;
                            </div>
                          </td>
                          <td>
                            <span
                              className="operation-pill-badge"
                              style={{
                                backgroundColor: op.bg,
                                color: op.color,
                                border: `1px solid ${op.border}`
                              }}
                            >
                              <OpIcon size={11} />
                              <span>{op.iconText}</span>
                            </span>
                          </td>
                          <td className="text-right">
                            <button
                              type="button"
                              className="btn-action-review"
                              onClick={() => openReviewModal(item)}
                            >
                              <Eye size={13} />
                              <span>Review</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        {/* 5. FAST REVIEW ACCESS HUB */}
        <section className="wa-section-card">
          <div className="wa-section-header">
            <div>
              <h2 className="wa-section-title">Akses Cepat Peninjauan Modul Utama</h2>
              <p className="wa-section-subtitle">
                Pilih modul di bawah untuk langsung menyaring antrean persetujuan pada halaman terkait
              </p>
            </div>
          </div>

          <div className="wa-quick-actions-grid">
            {quickAccessCards.map(card => {
              const CardIcon = card.icon;
              return (
                <div
                  key={card.key}
                  className="wa-action-card"
                  onClick={() => {
                    if (card.isTestimonial) {
                      setTestimonialStatusFilter(card.pending > 0 ? 'pending' : 'all');
                      setTestimonialActionFilter('all');
                      setActiveTab('testimonial-approvals');
                    } else {
                      setSelectedMenuFilter(card.key);
                      setSelectedStatusFilter(card.pending > 0 ? 'pending' : 'all');
                      setActiveTab('approvals');
                    }
                  }}
                >
                  <div className="pr-action-card-top">
                    <div className={`wa-action-icon ${card.iconClass}`} style={{ marginBottom: 0 }}>
                      <CardIcon size={20} />
                    </div>
                    {card.pending > 0 ? (
                      <span className="status-badge status-pending">
                        <Clock size={11} />
                        <span>{card.pending} Pending</span>
                      </span>
                    ) : (
                      <span className="wa-badge-status-published">
                        <span className="status-dot-mini" />
                        <span>Tersinkronisasi</span>
                      </span>
                    )}
                  </div>

                  <div className="wa-action-content" style={{ marginTop: '14px' }}>
                    <h3 className="wa-action-title">{card.title}</h3>
                    <p className="wa-action-desc">{card.desc}</p>
                  </div>

                  <div className="wa-action-footer" style={{ justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b', fontWeight: 500, fontSize: '0.75rem' }}>
                      {card.approved} Disetujui
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                      <span>{card.isTestimonial ? 'Buka Testimonials' : 'Buka Antrean'}</span>
                      <ArrowRight size={13} />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* 6. DIRECTORY TABLE OF ALL WEBSITE MODULES & TESTIMONIALS */}
        <section className="wa-section-card">
          <div className="wa-section-header">
            <div>
              <h2 className="wa-section-title">Direktori Status Modul Website &amp; Testimonials</h2>
              <p className="wa-section-subtitle">
                Ringkasan lengkap status antrean persetujuan pada 11 menu halaman website dan menu khusus Testimonials
              </p>
            </div>
          </div>

          <div className="wa-table-responsive">
            <table className="wa-directory-table">
              <thead>
                <tr>
                  <th>Modul Halaman</th>
                  <th>Cakupan Konten</th>
                  <th className="text-center">Menunggu Review</th>
                  <th className="text-center">Total Disetujui</th>
                  <th className="text-center">Status Publik</th>
                  <th className="text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {moduleDirectory.map((mod, index) => {
                  const pendingCount = mod.isTestimonial
                    ? testimonialStatusCounts.pending || 0
                    : menuStats[mod.key] || 0;
                  const approvedCount = mod.isTestimonial
                    ? testimonialStatusCounts.approved || 0
                    : menuStats.approvedByMenu?.[mod.key] || 0;

                  return (
                    <tr key={mod.key}>
                      <td className="wa-td-title">
                        <span className="wa-dir-number">{index + 1}</span>
                        <span className="wa-dir-title">{mod.name}</span>
                      </td>
                      <td className="wa-td-desc">{mod.desc}</td>
                      <td className="text-center">
                        {pendingCount > 0 ? (
                          <span className="status-badge status-pending">
                            <Clock size={11} />
                            <span>{pendingCount} Pending</span>
                          </span>
                        ) : (
                          <span className="wa-badge-sections">0 Pending</span>
                        )}
                      </td>
                      <td className="text-center">
                        <span className="wa-badge-sections">{approvedCount} Disetujui</span>
                      </td>
                      <td className="text-center">
                        {pendingCount > 0 ? (
                          <span className="status-badge status-pending">Menunggu Review</span>
                        ) : (
                          <span className="wa-badge-status-published">
                            <span className="status-dot-mini" />
                            Tersinkronisasi
                          </span>
                        )}
                      </td>
                      <td className="text-right">
                        <button
                          type="button"
                          className="wa-btn-table-edit"
                          onClick={() => {
                            if (mod.isTestimonial) {
                              setTestimonialStatusFilter(pendingCount > 0 ? 'pending' : 'all');
                              setTestimonialActionFilter('all');
                              setActiveTab('testimonial-approvals');
                            } else {
                              setSelectedMenuFilter(mod.key);
                              setSelectedStatusFilter(pendingCount > 0 ? 'pending' : 'all');
                              setActiveTab('approvals');
                            }
                          }}
                        >
                          <span>{mod.isTestimonial ? 'Menu Testimonials' : 'Lihat Submissions'}</span>
                          <ArrowRight size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* 7. PRINCIPAL APPROVAL WORKFLOW GUIDE */}
        <section className="wa-guide-card">
          <div className="wa-guide-header">
            <Info size={18} className="wa-guide-info-icon" />
            <h3 className="wa-guide-title">Panduan Otorisasi &amp; Moderasi Principal (Approval Workflow)</h3>
          </div>
          <div className="wa-guide-steps-grid">
            <div className="wa-guide-step">
              <div className="wa-step-badge">1</div>
              <div className="wa-step-body">
                <strong>Pilih Kategori Antrean</strong>
                <p>Gunakan menu sidebar <strong>Submissions</strong> untuk meninjau perubahan 11 menu website, atau menu <strong>Testimonials</strong> untuk memoderasi ulasan publik.</p>
              </div>
            </div>

            <div className="wa-guide-step">
              <div className="wa-step-badge">2</div>
              <div className="wa-step-body">
                <strong>Bandingkan Versi Sebelum &amp; Sesudah</strong>
                <p>Klik tombol <strong>Review</strong> untuk memeriksa perbandingan teks, gambar, atau status publikasi testimoni secara berdampingan.</p>
              </div>
            </div>

            <div className="wa-guide-step">
              <div className="wa-step-badge">3</div>
              <div className="wa-step-body">
                <strong>Setujui atau Kembalikan dengan Catatan</strong>
                <p>Klik <strong>Approve &amp; Publish</strong> untuk menayangkan langsung ke website publik, atau <strong>Reject</strong> beserta catatan revisi untuk Website Admin.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  };

  /**
   * TAB 2: WEBSITE MENU SUBMISSIONS QUEUE (EXCLUDES TESTIMONIALS)
   */
  const renderSubmissionsTable = () => {
    return (
      <div className="submissions-page-wrapper">
        {/* SUBMISSIONS HEADER */}
        <header className="page-main-header">
          <div>
            <div className="header-breadcrumbs">Principal Portal &gt; Submissions (All Website Menus)</div>
            <h1 className="header-title">Website Menu Submissions Queue</h1>
            <p className="header-subtitle">
              Daftar pengajuan perubahan konten dari 11 menu utama website (di luar ulasan testimoni). Bandingkan versi sebelum dan sesudah sebelum menyetujui atau menolak.
            </p>
          </div>
          <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              className="pr-switch-queue-btn"
              onClick={() => setActiveTab('testimonial-approvals')}
              title="Buka antrean khusus Testimonials"
            >
              <Quote size={14} />
              <span>Menu Testimonials ({testimonialStatusCounts.pending || 0} Pending)</span>
            </button>
            <button className="refresh-button" onClick={() => loadSubmissions('all')} disabled={loading} title="Reload list">
              <RefreshCw size={15} className={loading ? 'spinning' : ''} />
              <span>Refresh</span>
            </button>
          </div>
        </header>

        {/* STATUS TABS (PENDING, APPROVED, REJECTED, ALL) FOR WEBSITE MENUS */}
        <div className="status-tabs-container">
          <button
            className={`status-tab-btn ${selectedStatusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => {
              setSelectedStatusFilter('pending');
              loadSubmissions('all');
            }}
          >
            <Clock size={15} />
            <span>Pending Review</span>
            <span className="status-count-badge badge-pending">{contentStatusCounts.pending || 0}</span>
          </button>

          <button
            className={`status-tab-btn ${selectedStatusFilter === 'approved' ? 'active' : ''}`}
            onClick={() => {
              setSelectedStatusFilter('approved');
              loadSubmissions('all');
            }}
          >
            <CheckCircle2 size={15} />
            <span>Approved</span>
            <span className="status-count-badge badge-approved">{contentStatusCounts.approved || 0}</span>
          </button>

          <button
            className={`status-tab-btn ${selectedStatusFilter === 'rejected' ? 'active' : ''}`}
            onClick={() => {
              setSelectedStatusFilter('rejected');
              loadSubmissions('all');
            }}
          >
            <XCircle size={15} />
            <span>Rejected</span>
            <span className="status-count-badge badge-rejected">{contentStatusCounts.rejected || 0}</span>
          </button>

          <button
            className={`status-tab-btn ${selectedStatusFilter === 'all' ? 'active' : ''}`}
            onClick={() => {
              setSelectedStatusFilter('all');
              loadSubmissions('all');
            }}
          >
            <ClipboardList size={15} />
            <span>All Menu Submissions</span>
            <span className="status-count-badge badge-all">
              {(contentStatusCounts.pending || 0) + (contentStatusCounts.approved || 0) + (contentStatusCounts.rejected || 0)}
            </span>
          </button>
        </div>

        {/* TOOLBAR FILTER & SEARCH */}
        <div className="toolbar-container">
          <div className="menu-filter-tabs">
            {[
              { key: 'all', label: 'All Menus', icon: ClipboardList },
              { key: 'homepage', label: 'Homepage', icon: Home },
              { key: 'about-us', label: 'About Us', icon: Landmark },
              { key: 'education-program', label: 'Education', icon: GraduationCap },
              { key: 'admissions', label: 'Admissions', icon: FileEdit },
              { key: 'student-life', label: 'Student Life', icon: Activity },
              { key: 'facilities', label: 'Facilities', icon: Building2 },
              { key: 'news-updates', label: 'News & Updates', icon: Newspaper },
              { key: 'parents', label: 'Parents', icon: Users },
              { key: 'careers', label: 'Careers', icon: Briefcase },
              { key: 'contact-us', label: 'Contact Us', icon: Phone },
              { key: 'footer', label: 'Footer', icon: PanelBottom },
            ].map(tab => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.key}
                  className={`menu-filter-pill ${selectedMenuFilter === tab.key ? 'active' : ''}`}
                  onClick={() => setSelectedMenuFilter(tab.key)}
                >
                  <TabIcon size={14} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          <div className="search-bar-wrap">
            <Search size={15} className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search field, section, submitter..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear-btn" onClick={() => setSearchQuery('')} title="Clear search">
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {/* ERROR STATE */}
        {error && (
          <div className="approval-error-banner">
            <AlertTriangle size={18} className="error-icon" />
            <span>Error loading submissions: {error}</span>
            <button className="btn-retry" onClick={() => loadSubmissions('all')}>Retry</button>
          </div>
        )}

        {/* LOADING STATE */}
        {loading ? (
          <div className="approval-loading-container">
            <RefreshCw size={26} className="spinning text-primary" />
            <p>Loading menu submissions...</p>
          </div>
        ) : filteredSubmissions.length === 0 ? (
          /* EMPTY STATE */
          <div className="approval-empty-container">
            <CheckCircle2 size={44} color="#10b981" />
            <h3>No Menu Submissions Found</h3>
            <p>
              {searchQuery || selectedMenuFilter !== 'all'
                ? 'No menu submissions match your active filter or search keywords.'
                : selectedStatusFilter === 'pending'
                  ? 'There are currently no pending website menu submissions awaiting review.'
                  : `No ${selectedStatusFilter} website menu submissions available.`}
            </p>
            {(searchQuery || selectedMenuFilter !== 'all') && (
              <button
                className="btn-reset-filter"
                onClick={() => {
                  setSelectedMenuFilter('all');
                  setSearchQuery('');
                }}
              >
                Reset Filters
              </button>
            )}
          </div>
        ) : (
          /* PURE TABLE VIEW */
          <div className="submissions-table-card">
            <div className="table-responsive">
              <table className="submissions-main-table">
                <colgroup>
                  <col style={{ width: '70px' }} />
                  <col style={{ width: '130px' }} />
                  <col style={{ width: '155px' }} />
                  <col style={{ width: '125px' }} />
                  <col style={{ width: '220px' }} />
                  <col style={{ width: '170px' }} />
                  <col style={{ width: '135px' }} />
                  <col style={{ width: '105px' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Menu</th>
                    <th>Section</th>
                    <th>Action</th>
                    <th>Target Field</th>
                    <th>Submitted By</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'center' }}>Review</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubmissions.map(item => {
                    const menu = getMenuCategory(item.page, item.section_type, item.field_name, item.field_id);
                    const MenuIcon = menu.icon;
                    const sectionName = getSectionDisplayName(item.section_type, item.field_name, item.field_id);
                    const fieldDisplay = formatFieldDisplayName(item.field_name, item.field_id);
                    const op = getOperationInfo(item.field_id, item.old_value, item.new_value, item.field_name);
                    const OpIcon = op.icon;
                    const formattedDate = new Date(item.submitted_at || Date.now()).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    });

                    return (
                      <tr key={item.id} className="submission-table-row">
                        <td className="id-cell">
                          <span className="id-badge">#{item.id}</span>
                        </td>
                        <td className="menu-cell">
                          <span
                            className="menu-badge"
                            style={{
                              backgroundColor: menu.bg,
                              color: menu.color,
                              border: `1px solid ${menu.border}`,
                            }}
                          >
                            <MenuIcon size={13} />
                            <span>{menu.name}</span>
                          </span>
                        </td>
                        <td className="section-cell">
                          <div className="section-cell-text" title={sectionName}>
                            {sectionName}
                          </div>
                        </td>
                        <td className="action-type-cell">
                          <span
                            className="operation-pill-badge"
                            style={{
                              backgroundColor: op.bg,
                              color: op.color,
                              border: `1px solid ${op.border}`
                            }}
                          >
                            <OpIcon size={12} />
                            <span>{op.iconText}</span>
                          </span>
                        </td>
                        <td className="field-cell">
                          <div className="field-cell-content">
                            <span className="field-name-text" title={fieldDisplay}>{fieldDisplay}</span>
                            <span className="field-code-sub" title={item.field_id}>{item.field_id}</span>
                          </div>
                        </td>
                        <td className="submitter-cell">
                          <div className="submitter-cell-content">
                            <div className="submitter-name" title={item.submitted_by_name || 'Website Admin'}>
                              {item.submitted_by_name || 'Website Admin'}
                            </div>
                            <div className="submitter-date">
                              <Calendar size={11} style={{ display: 'inline', marginRight: '4px', verticalAlign: '-1px' }} />
                              <span>{formattedDate}</span>
                            </div>
                          </div>
                        </td>
                        <td className="status-cell">
                          {item.status === 'approved' ? (
                            <span className="status-badge status-approved">
                              <CheckCircle2 size={12} />
                              <span>Approved</span>
                            </span>
                          ) : item.status === 'rejected' ? (
                            <span className="status-badge status-rejected">
                              <XCircle size={12} />
                              <span>Rejected</span>
                            </span>
                          ) : (
                            <span className="status-badge status-pending">
                              <Clock size={12} />
                              <span>Pending Review</span>
                            </span>
                          )}
                        </td>
                        <td className="action-cell" style={{ textAlign: 'center' }}>
                          <button
                            className={`btn-action-review ${item.status !== 'pending' ? 'btn-action-view' : ''}`}
                            onClick={() => openReviewModal(item)}
                            title={item.status === 'pending' ? 'Review changes' : 'View details'}
                          >
                            <Eye size={14} />
                            <span>{item.status === 'pending' ? 'Review' : 'Details'}</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="table-footer-info">
              <span>Showing {filteredSubmissions.length} website menu submissions</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  /**
   * TAB 3: DEDICATED TESTIMONIALS SUBMISSIONS QUEUE
   */
  const renderTestimonialsSubmissionsTable = () => {
    return (
      <div className="submissions-page-wrapper">
        {/* TESTIMONIALS SUBMISSIONS HEADER */}
        <header className="page-main-header">
          <div>
            <div className="header-breadcrumbs">Principal Portal &gt; Testimonials Submissions</div>
            <h1 className="header-title">Testimonials Submissions Queue</h1>
            <p className="header-subtitle">
              Antrean khusus moderasi ulasan testimoni orang tua, siswa, dan alumni (Publish ON, Unpublish OFF, Delete Item, maupun pembaruan kutipan).
            </p>
          </div>
          <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              className="pr-switch-queue-btn"
              onClick={() => setActiveTab('approvals')}
              title="Buka antrean Submissions Semua Menu Website"
            >
              <ClipboardList size={14} />
              <span>Submissions Menu ({contentStatusCounts.pending || 0} Pending)</span>
            </button>
            <button
              className="refresh-button"
              onClick={() => {
                loadSubmissions('all');
                loadTestimonialsMap();
              }}
              disabled={loading}
              title="Reload testimonials queue"
            >
              <RefreshCw size={15} className={loading ? 'spinning' : ''} />
              <span>Refresh</span>
            </button>
          </div>
        </header>

        {/* STATUS TABS FOR TESTIMONIALS */}
        <div className="status-tabs-container">
          <button
            className={`status-tab-btn ${testimonialStatusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => {
              setTestimonialStatusFilter('pending');
              loadSubmissions('all');
            }}
          >
            <Clock size={15} />
            <span>Pending Review</span>
            <span className="status-count-badge badge-pending">{testimonialStatusCounts.pending || 0}</span>
          </button>

          <button
            className={`status-tab-btn ${testimonialStatusFilter === 'approved' ? 'active' : ''}`}
            onClick={() => {
              setTestimonialStatusFilter('approved');
              loadSubmissions('all');
            }}
          >
            <CheckCircle2 size={15} />
            <span>Approved</span>
            <span className="status-count-badge badge-approved">{testimonialStatusCounts.approved || 0}</span>
          </button>

          <button
            className={`status-tab-btn ${testimonialStatusFilter === 'rejected' ? 'active' : ''}`}
            onClick={() => {
              setTestimonialStatusFilter('rejected');
              loadSubmissions('all');
            }}
          >
            <XCircle size={15} />
            <span>Rejected</span>
            <span className="status-count-badge badge-rejected">{testimonialStatusCounts.rejected || 0}</span>
          </button>

          <button
            className={`status-tab-btn ${testimonialStatusFilter === 'all' ? 'active' : ''}`}
            onClick={() => {
              setTestimonialStatusFilter('all');
              loadSubmissions('all');
            }}
          >
            <Quote size={15} />
            <span>All Testimonials</span>
            <span className="status-count-badge badge-all">
              {(testimonialStatusCounts.pending || 0) + (testimonialStatusCounts.approved || 0) + (testimonialStatusCounts.rejected || 0)}
            </span>
          </button>
        </div>

        {/* TESTIMONIALS ACTION FILTER & SEARCH TOOLBAR */}
        <div className="toolbar-container">
          <div className="menu-filter-tabs">
            {[
              { key: 'all', label: 'All Testimonial Actions', icon: Quote },
              { key: 'publish', label: 'Publish (ON)', icon: Power },
              { key: 'unpublish', label: 'Unpublish (OFF)', icon: EyeOff },
              { key: 'delete', label: 'Delete Testimonial', icon: Trash2 },
              { key: 'update', label: 'Section / Content Update', icon: FileEdit }
            ].map(tab => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.key}
                  className={`menu-filter-pill ${testimonialActionFilter === tab.key ? 'active' : ''}`}
                  onClick={() => setTestimonialActionFilter(tab.key)}
                >
                  <TabIcon size={14} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          <div className="search-bar-wrap">
            <Search size={15} className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Cari nama pemberi ulasan, kutipan, role..."
              value={testimonialSearchQuery}
              onChange={e => setTestimonialSearchQuery(e.target.value)}
            />
            {testimonialSearchQuery && (
              <button className="search-clear-btn" onClick={() => setTestimonialSearchQuery('')} title="Clear search">
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {/* ERROR STATE */}
        {error && (
          <div className="approval-error-banner">
            <AlertTriangle size={18} className="error-icon" />
            <span>Error loading testimonial submissions: {error}</span>
            <button className="btn-retry" onClick={() => loadSubmissions('all')}>Retry</button>
          </div>
        )}

        {/* LOADING STATE */}
        {loading ? (
          <div className="approval-loading-container">
            <RefreshCw size={26} className="spinning text-primary" />
            <p>Loading testimonial submissions...</p>
          </div>
        ) : filteredTestimonialSubmissions.length === 0 ? (
          /* EMPTY STATE */
          <div className="approval-empty-container">
            <CheckCircle2 size={44} color="#10b981" />
            <h3>No Testimonial Submissions Found</h3>
            <p>
              {testimonialSearchQuery || testimonialActionFilter !== 'all'
                ? 'Tidak ada pengajuan testimoni yang cocok dengan filter aksi atau kata kunci pencarian Anda.'
                : testimonialStatusFilter === 'pending'
                  ? 'Saat ini tidak ada pengajuan perubahan atau moderasi testimoni yang menunggu persetujuan Principal.'
                  : `Tidak ada riwayat pengajuan testimoni berstatus ${testimonialStatusFilter}.`}
            </p>
            {(testimonialSearchQuery || testimonialActionFilter !== 'all') && (
              <button
                className="btn-reset-filter"
                onClick={() => {
                  setTestimonialActionFilter('all');
                  setTestimonialSearchQuery('');
                }}
              >
                Reset Filters
              </button>
            )}
          </div>
        ) : (
          /* DEDICATED TESTIMONIALS TABLE VIEW */
          <div className="submissions-table-card">
            <div className="table-responsive">
              <table className="submissions-main-table">
                <colgroup>
                  <col style={{ width: '70px' }} />
                  <col style={{ width: '195px' }} />
                  <col style={{ width: '340px' }} />
                  <col style={{ width: '135px' }} />
                  <col style={{ width: '165px' }} />
                  <col style={{ width: '135px' }} />
                  <col style={{ width: '110px' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Author &amp; Role</th>
                    <th>Testimonial Quote / Content</th>
                    <th>Requested Action</th>
                    <th>Submitted By</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'center' }}>Review</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTestimonialSubmissions.map(item => {
                    const tInfo = extractTestimonialInfo(item);
                    const op = getOperationInfo(item.field_id, item.old_value, item.new_value, item.field_name);
                    const OpIcon = op.icon;
                    const formattedDate = new Date(item.submitted_at || Date.now()).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    });

                    return (
                      <tr key={item.id} className="submission-table-row">
                        <td className="id-cell">
                          <span className="id-badge">#{item.id}</span>
                        </td>
                        <td className="section-cell">
                          <div className="pr-author-cell">
                            <span className="pr-author-name" title={tInfo.authorName}>
                              {tInfo.authorName}
                            </span>
                            <div className="pr-author-meta-row">
                              <span className="pr-author-role-badge">{tInfo.authorRole}</span>
                              {tInfo.testimonialId && (
                                <span className="pr-testimonial-id-chip">ID #{tInfo.testimonialId}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="field-cell">
                          <div className="pr-quote-cell">
                            <p className="pr-quote-preview-text" title={tInfo.quoteText}>
                              &ldquo;{tInfo.quoteText}&rdquo;
                            </p>
                            {item.status === 'rejected' && item.rejected_reason && (
                              <span className="pr-quote-reject-reason">
                                Alasan Ditolak: &ldquo;{item.rejected_reason}&rdquo;
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="action-type-cell">
                          <span
                            className="operation-pill-badge"
                            style={{
                              backgroundColor: op.bg,
                              color: op.color,
                              border: `1px solid ${op.border}`
                            }}
                          >
                            <OpIcon size={12} />
                            <span>{op.iconText}</span>
                          </span>
                        </td>
                        <td className="submitter-cell">
                          <div className="submitter-cell-content">
                            <div className="submitter-name" title={item.submitted_by_name || 'Website Admin'}>
                              {item.submitted_by_name || 'Website Admin'}
                            </div>
                            <div className="submitter-date">
                              <Calendar size={11} style={{ display: 'inline', marginRight: '4px', verticalAlign: '-1px' }} />
                              <span>{formattedDate}</span>
                            </div>
                          </div>
                        </td>
                        <td className="status-cell">
                          {item.status === 'approved' ? (
                            <span className="status-badge status-approved">
                              <CheckCircle2 size={12} />
                              <span>Approved</span>
                            </span>
                          ) : item.status === 'rejected' ? (
                            <span className="status-badge status-rejected">
                              <XCircle size={12} />
                              <span>Rejected</span>
                            </span>
                          ) : (
                            <span className="status-badge status-pending">
                              <Clock size={12} />
                              <span>Pending Review</span>
                            </span>
                          )}
                        </td>
                        <td className="action-cell" style={{ textAlign: 'center' }}>
                          <button
                            className={`btn-action-review ${item.status !== 'pending' ? 'btn-action-view' : ''}`}
                            onClick={() => openReviewModal(item)}
                            title={item.status === 'pending' ? 'Review testimonial submission' : 'View details'}
                          >
                            <Eye size={14} />
                            <span>{item.status === 'pending' ? 'Review' : 'Details'}</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="table-footer-info">
              <span>Showing {filteredTestimonialSubmissions.length} testimonial submissions</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  /**
   * REVIEW MODAL - MOUNTED VIA REACT PORTAL TO DOCUMENT.BODY
   * Never covered by sidebar or navbar!
   */
  const renderModalPortal = () => {
    if (!showModal || !selectedItem) return null;

    const menu = getMenuCategory(selectedItem.page, selectedItem.section_type, selectedItem.field_name, selectedItem.field_id);
    const MenuIcon = menu.icon;
    const sectionName = getSectionDisplayName(selectedItem.section_type, selectedItem.field_name, selectedItem.field_id);
    const fieldDisplay = formatFieldDisplayName(selectedItem.field_name, selectedItem.field_id);
    const isReadOnly = selectedItem.status === 'approved' || selectedItem.status === 'rejected';

    const modalContent = (
      <div className="modal-portal-overlay" onClick={closeModal}>
        <div className="approval-detail-modal" onClick={e => e.stopPropagation()}>
          {/* MODAL HEADER */}
          <div className="detail-modal-header">
            <div className="modal-header-top-row">
              <div className="modal-headline-group">
                <h2 className="modal-headline">
                  {isReadOnly ? `Submission Details #${selectedItem.id}` : `Review Submission #${selectedItem.id}`}
                </h2>
                {selectedItem.status === 'approved' ? (
                  <span className="status-badge status-approved">
                    <CheckCircle2 size={13} />
                    <span>Approved</span>
                  </span>
                ) : selectedItem.status === 'rejected' ? (
                  <span className="status-badge status-rejected">
                    <XCircle size={13} />
                    <span>Rejected</span>
                  </span>
                ) : (
                  <span className="status-badge status-pending">
                    <Clock size={13} />
                    <span>Pending Review</span>
                  </span>
                )}
              </div>
              <button className="modal-btn-close" onClick={closeModal} title="Close modal" aria-label="Close modal">
                <X size={20} />
              </button>
            </div>

            {/* STRUCTURED METADATA CONTEXT BAR */}
            <div className="modal-context-bar">
              <div className="context-card">
                <span className="context-label">Location (Menu &rsaquo; Section)</span>
                <div className="context-value">
                  <span
                    className="menu-badge-compact"
                    style={{
                      backgroundColor: menu.bg,
                      color: menu.color,
                      border: `1px solid ${menu.border}`,
                    }}
                  >
                    <MenuIcon size={12} />
                    <span>{menu.name}</span>
                  </span>
                  <span className="context-arrow">&rsaquo;</span>
                  <strong className="context-section-name">{sectionName}</strong>
                </div>
              </div>

              <div className="context-card">
                <span className="context-label">Action / Operation</span>
                <div className="context-value">
                  {(() => {
                    const modalOp = getOperationInfo(selectedItem.field_id, selectedItem.old_value, selectedItem.new_value, selectedItem.field_name);
                    const ModalOpIcon = modalOp.icon;
                    return (
                      <span
                        className="operation-pill-badge"
                        style={{
                          backgroundColor: modalOp.bg,
                          color: modalOp.color,
                          border: `1px solid ${modalOp.border}`,
                          fontWeight: 700
                        }}
                      >
                        <ModalOpIcon size={12} />
                        <span>{modalOp.actionTitle}</span>
                      </span>
                    );
                  })()}
                </div>
              </div>

              <div className="context-card">
                <span className="context-label">Target Field / Item</span>
                <div className="context-value">
                  <span className="context-field-name">{fieldDisplay}</span>
                  <span className="context-field-code">({selectedItem.field_id})</span>
                </div>
              </div>

              <div className="context-card">
                <span className="context-label">Submitted By</span>
                <div className="context-value">
                  <User size={13} className="context-icon" />
                  <span className="context-submitter-name">{selectedItem.submitted_by_name || 'Website Admin'}</span>
                </div>
              </div>

              <div className="context-card">
                <span className="context-label">Submission Date</span>
                <div className="context-value">
                  <Calendar size={13} className="context-icon" />
                  <span>
                    {new Date(selectedItem.submitted_at || Date.now()).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* MODAL BODY */}
          <div className="detail-modal-body">
            {/* IF REJECTED, SHOW THE REJECTION FEEDBACK CALLOUT ON TOP */}
            {selectedItem.status === 'rejected' && selectedItem.rejected_reason && (
              <div className="rejection-feedback-banner">
                <div className="feedback-banner-header">
                  <MessageSquare size={16} />
                  <strong>Rejection Feedback Reason:</strong>
                </div>
                <p className="feedback-banner-text">{selectedItem.rejected_reason}</p>
                {selectedItem.approved_by_name && (
                  <span className="feedback-reviewer">Reviewed by: {selectedItem.approved_by_name}</span>
                )}
              </div>
            )}

            {modalMode === 'view' && (
              <div className="comparison-container">
                <div className="comparison-column before-column">
                  <div className="comparison-column-header">
                    <div className="column-title-wrap">
                      <CheckCircle2 size={15} className="col-header-icon text-muted" />
                      <span className="column-label before-label">Current Published Version</span>
                    </div>
                    <span className="column-desc">Currently live and visible to the public</span>
                  </div>
                  <div className="comparison-content-box">
                    {renderValuePreview(selectedItem.old_value, selectedItem.field_id, true, selectedItem.new_value)}
                  </div>
                </div>

                <div className="comparison-column after-column">
                  <div className="comparison-column-header">
                    <div className="column-title-wrap">
                      <Sparkles size={15} className="col-header-icon text-primary" />
                      <span className="column-label after-label">
                        {selectedItem.status === 'approved' ? 'Approved Version' : 'Proposed New Changes'}
                      </span>
                    </div>
                    <span className="column-desc">Submitted by Website Admin for approval</span>
                  </div>
                  <div className="comparison-content-box">
                    {renderValuePreview(selectedItem.new_value, selectedItem.field_id, false, selectedItem.old_value)}
                  </div>
                </div>
              </div>
            )}

            {/* REJECT FORM MODE */}
            {modalMode === 'reject' && (
              <div className="rejection-form-container">
                <div className="rejection-alert-box">
                  <AlertTriangle size={20} className="rejection-alert-icon" />
                  <div>
                    <h4>Confirm Rejection with Feedback</h4>
                    <p>
                      Please provide a clear reason for rejecting this change. This feedback note will be returned to the
                      Website Admin so they can make the necessary revisions.
                    </p>
                  </div>
                </div>

                <div className="rejection-input-group">
                  <label htmlFor="rejectReasonText" className="rejection-label">
                    Rejection Reason (Required):
                  </label>
                  <textarea
                    id="rejectReasonText"
                    className="rejection-textarea"
                    rows={4}
                    placeholder="e.g. Please replace with an official landscape orientation photograph of the campus..."
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    autoFocus
                  />
                  <div className="char-counter">
                    {rejectReason.trim().length < 5 ? (
                      <span className="text-warning">Minimum 5 characters (currently: {rejectReason.trim().length})</span>
                    ) : (
                      <span className="text-success">{rejectReason.trim().length} characters entered</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* APPROVE CONFIRMATION MODE */}
            {modalMode === 'approve_confirm' && (
              <div className="approve-confirm-container">
                <div className="approve-confirm-icon-wrap">
                  <CheckCircle2 size={44} color="#059669" />
                </div>
                <h3>Confirm Live Publication</h3>
                <p>
                  Are you sure you want to approve submission <strong>#{selectedItem.id}</strong> for{' '}
                  <strong>{sectionName}</strong>?
                </p>
                <div className="approve-note-card">
                  <p>
                    These changes will be written directly to <code>content_published</code> and will immediately be live
                    on the public school website.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* MODAL FOOTER ACTIONS */}
          <div className="detail-modal-footer">
            {modalMode === 'view' && (
              <>
                <button
                  type="button"
                  className="btn-modal-cancel"
                  onClick={closeModal}
                >
                  Close
                </button>
                {!isReadOnly ? (
                  <div className="action-button-group">
                    <button
                      type="button"
                      className="btn-modal-reject"
                      onClick={() => setModalMode('reject')}
                    >
                      <XCircle size={15} />
                      <span>Reject</span>
                    </button>
                    <button
                      type="button"
                      className="btn-modal-approve"
                      onClick={() => setModalMode('approve_confirm')}
                    >
                      <CheckCircle2 size={15} />
                      <span>Approve & Publish</span>
                    </button>
                  </div>
                ) : (
                  <div className="readonly-status-tag">
                    <span className="text-muted">Status: <strong>{selectedItem.status.toUpperCase()}</strong></span>
                  </div>
                )}
              </>
            )}

            {modalMode === 'approve_confirm' && (
              <>
                <button
                  type="button"
                  className="btn-modal-cancel"
                  onClick={() => setModalMode('view')}
                  disabled={actionLoading}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn-modal-approve-final"
                  onClick={handleApprove}
                  disabled={actionLoading}
                >
                  <Check size={16} />
                  <span>{actionLoading ? 'Publishing Live...' : 'Confirm & Publish Live'}</span>
                </button>
              </>
            )}

            {modalMode === 'reject' && (
              <>
                <button
                  type="button"
                  className="btn-modal-cancel"
                  onClick={() => setModalMode('view')}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-modal-reject-final"
                  onClick={handleReject}
                  disabled={actionLoading || rejectReason.trim().length < 5}
                >
                  <XCircle size={15} />
                  <span>{actionLoading ? 'Saving Rejection...' : 'Confirm Rejection'}</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* FULLSCREEN LIGHTBOX OVERLAY */}
        {lightboxImage && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.88)',
              backdropFilter: 'blur(8px)',
              zIndex: 10000,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px'
            }}
            onClick={() => setLightboxImage(null)}
          >
            <div
              style={{
                position: 'relative',
                maxWidth: '92vw',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: '#0f172a',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.75)'
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 18px',
                backgroundColor: '#1e293b',
                color: '#f8fafc',
                boxSizing: 'border-box'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ImageIcon size={16} style={{ color: '#38bdf8' }} />
                  <strong style={{ fontSize: '13px' }}>{lightboxImage.title || 'Pratinjau Foto Resolusi Penuh'}</strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <a
                    href={lightboxImage.src}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#38bdf8',
                      fontSize: '12px',
                      fontWeight: '600',
                      textDecoration: 'none'
                    }}
                  >
                    <ExternalLink size={13} />
                    <span>Buka di Tab Baru</span>
                  </a>
                  <button
                    type="button"
                    onClick={() => setLightboxImage(null)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div style={{
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                maxHeight: 'calc(90vh - 60px)',
                overflow: 'auto',
                backgroundColor: '#020617'
              }}>
                <img
                  src={lightboxImage.src}
                  alt="Full resolution preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 'calc(85vh - 70px)',
                    objectFit: 'contain',
                    borderRadius: '6px'
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );

    return createPortal(modalContent, document.body);
  };

  return (
    <DashboardLayout
      user={user}
      onLogout={onLogout}
      activeTab={activeTab}
      onTabChange={tab => setActiveTab(tab)}
      pendingCount={contentStatusCounts.pending || 0}
      pendingTestimonialsCount={testimonialStatusCounts.pending || 0}
      pendingFeesCount={admissionsSubmenuPending.total || pendingFeeLevels.length}
      admissionsSubmenuPending={admissionsSubmenuPending}
      userRole={effectiveRole}
    >
      <div className="approval-page-container">
        {/* TOAST FEEDBACK */}
        {toastMessage && (
          <div className={`approval-toast ${toastMessage.type}`}>
            <span className="toast-text">{toastMessage.text}</span>
            <button className="toast-dismiss" onClick={() => setToastMessage(null)}>
              <X size={15} />
            </button>
          </div>
        )}

        {/* TAB RENDERING */}
        {activeTab === 'dashboard' ? (
          renderDashboardOverview()
        ) : activeTab === 'testimonial-approvals' ? (
          renderTestimonialsSubmissionsTable()
        ) : activeTab === 'admissions-overview' ? (
          <AdmissionsExecutiveOverview
            apiBase={apiBase}
            userRole={effectiveRole}
            onNavigateTab={(tab) => setActiveTab(tab)}
          />
        ) : activeTab === 'admissions-periods' ? (
          <AdmissionsFeeApprovalsTab
            apiBase={apiBase}
            initialCategory="periods"
            userRole={effectiveRole}
            onFeeChange={() => {
              loadSubmissions('all');
              loadPendingFeeLevels();
            }}
          />
        ) : activeTab === 'admissions-fee-approvals' ? (
          <AdmissionsFeeApprovalsTab
            apiBase={apiBase}
            initialCategory="levels"
            userRole={effectiveRole}
            onFeeChange={() => {
              loadSubmissions('all');
              loadPendingFeeLevels();
            }}
          />
        ) : activeTab === 'admissions-test-slots' ? (
          <AdmissionsFeeApprovalsTab
            apiBase={apiBase}
            initialCategory="test_slots"
            userRole={effectiveRole}
            onFeeChange={() => {
              loadSubmissions('all');
              loadPendingFeeLevels();
            }}
          />
        ) : activeTab === 'admissions-promo-codes' ? (
          <AdmissionsFeeApprovalsTab
            apiBase={apiBase}
            initialCategory="promo_codes"
            userRole={effectiveRole}
            onFeeChange={() => {
              loadSubmissions('all');
              loadPendingFeeLevels();
            }}
          />
        ) : (
          renderSubmissionsTable()
        )}

        {/* PORTAL MODAL (MOUNTED TO BODY, ZERO CLIPPING) */}
        {renderModalPortal()}
      </div>
    </DashboardLayout>
  );
}
